/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Annotation, AnnotationType } from "@/lib/api";
import type { AnnotationTool, EraseMode } from "@/stores/annotation-tool";

import {
  DEFAULT_FONT_SIZE_REL,
  MIN_FONT_PX,
  TEXT_LINE_HEIGHT,
  fontStack,
} from "./fonts";
import { hasPressure, strokeOutline, traceSmooth, type SmoothingPreset } from "./ink";
import {
  absBox,
  absSegment,
  arrowHead,
  boxFromDrag,
  boxShapePath,
  dashPattern,
  diamondPoints,
  evenTracks,
  normalizeTracks,
  resizeTrack,
  tableLineAt,
  tablePath,
  tableTracksForBox,
  ellipseParams,
  isBoxShape,
  isSegmentShape,
  outlinePoints,
  shapeStrokeWidth,
  type AbsBox,
  type BoxShape,
  type Pt,
  type SegmentShape,
} from "./shapes";
import { computeFill, type FillFailure, type FillPoint } from "./flood-fill";
import { clampWritableX, clampWritableY, isWritablePoint } from "./infinite-canvas-math";
import { touchDrawAllowed } from "./input-policy";
import type { BufferedSample } from "./pen-buffer";
import { fmtPtr, penDebugEnabled, penLog } from "./pen-debug";
import { type Bbox, SpatialGrid } from "./spatial-index";
import { hasSeenPen, markPen, msSincePen, setPenStrokeActive } from "./stylus-state";

/** Editable Fabric overlay for ONE PDF page (Milestone B).
 *
 * Lazy-loaded (Fabric is code-split, never on the reading path) and fully
 * self-contained: all geometry is normalized to page-relative [0,1] on save
 * and denormalized to the page's current pixel size on load, so markup tracks
 * zoom/DPI exactly — same contract as the read-only overlay. The controller
 * never imports the graph/AI/search code; persistence is via injected
 * handlers, so a failure here can't reach anything else.
 *
 * INPUT MODEL (GoodNotes/Notability-style — "pen writes, fingers navigate,
 * hover previews, selection is explicit"): the pen stroke is hand-rolled rather
 * than using Fabric's PencilBrush, and the pen is deliberately NOT pointer-
 * captured (capture broke rapid lift-and-return writing on iPad — see
 * onPointerDown). Ink is produced ONLY for the one active pointer between its
 * down and up — a hovering pen (move with no contact) NEVER marks, and a missed
 * up can't leave the canvas drawing. In pen mode a pen/mouse writes, a finger
 * pans once a stylus has been seen (else a finger writes, for pure-touch
 * devices), and a palm is ignored.
 *
 * iPad robustness: WebKit can drop the PointerEvent stream of a rapid second
 * Pencil contact entirely while still exposing it through Touch Events or
 * compatibility mouse events. Pointer Events stay the primary path; the
 * touch/mouse fallback handlers and the contact-bearing-pointermove recovery
 * activate ONLY when no pointer stroke is active, then feed the same
 * sample/commit pipeline, so no stroke is silently lost. */

export interface AnnotationDraft {
  page: number;
  type: AnnotationType;
  points?: { x: number; y: number; pressure?: number }[];
  text?: string;
  color?: string;
  font_family?: string;
  font_size_rel?: number;
  stroke_rel?: number;
  opacity?: number;
  rect?: { x: number; y: number; w: number; h: number };
  /** fill: traced polygon rings in page-relative [0,1] (even-odd). */
  rings?: { x: number; y: number }[][];
  /** table: column widths and row heights, as fractions of the table's box. */
  cols?: number[];
  rows?: number[];
  /** shape styling. */
  fill_color?: string;
  dash?: boolean;
  corner_radius?: number;
}

/** An ordered set of markup mutations the editor commits as ONE transaction (an
 * eraser drag, an area-split). Structurally identical to the InkModel's `TxOps`,
 * but declared here so the editor stays decoupled from the model — ReaderTab wires
 * `onTransact` to `model.transact`. */
export interface TransactOps {
  creates?: AnnotationDraft[];
  updates?: { id: string; patch: Partial<AnnotationDraft> }[];
  deletes?: string[];
}

export interface PersistHandlers {
  /** Register a new annotation and get its client temp id back SYNCHRONOUSLY: the
   * editor tags its Fabric object immediately (no await), and the InkModel maps the
   * temp id to the server id on flush (routed back via applyIdChange). Persistence,
   * batching, and offline replay all live in the model now — not here. */
  onCreate: (draft: AnnotationDraft) => string;
  onUpdate: (id: string, patch: Partial<AnnotationDraft>) => void;
  onDelete: (id: string) => void;
  /** Commit an ordered op set as ONE model transaction → one undo entry → one
   * batch write, returning the created annotations' temp ids in order. The eraser
   * drag commits through this so a whole sweep (deletes + area-splits) is a single
   * write, not a mid-drag fan-out (plan F2). Optional so the editor still works
   * against a bare create/update/delete triple (it falls back to those). */
  onTransact?: (ops: TransactOps) => string[];
  /** The paint bucket had nothing to fill — the click landed on a stroke, in an
   * area that isn't enclosed, or on a sliver. The reader turns this into a short
   * explanation; silence would just look broken. */
  onFillRefused?: (reason: FillFailure) => void;
  /** The lasso tool circled a region: a base64 PNG "scan" of the underlying page
   * area, its page-relative rect, and the page number — handed to AI explain. */
  onRegion?: (imageB64: string, rect: { x: number; y: number; w: number; h: number }, page: number) => void;
}

/** Longest edge of the raster the paint bucket floods. A fill is a whole-surface
 * operation per click; beyond this the extra pixels cost time and change nothing
 * anyone can see (the result is simplified into vectors regardless). */
const FILL_MAX_DIM = 1400;

export interface EditorOpts {
  color: string;
  font: string;
  tool: AnnotationTool;
  fontSizeRel: number;
  strokeRel: number;
  highlighterRel: number;
  eraserRel: number;
  eraseMode: EraseMode;
  pressure: boolean;
  smoothing: SmoothingPreset;
  // An editor that mounts mid-session must carry the live shape style too, or the
  // next shape it draws is styled with the defaults until something changes. This
  // matters once editors mount and unmount as pages scroll.
  shapeFill: string | null;
  shapeDash: boolean;
  shapeCorner: number;
}

/**
 * Whether an ink object keeps Fabric's per-object offscreen cache canvas.
 *
 * Fabric's default is `true`: one extra canvas per stroke, drawn once and then
 * blitted. That path held about 60 percent of the write-mode entry on a 20-page,
 * 20,000-stroke note. `false` drops the canvas and re-tessellates the path on every
 * render instead, which is what shapes, tables and fills already do in production
 * (see their constructors), and it also removes the 2,097,152 px cache cap that
 * makes a long stroke render soft.
 *
 * The trade is that every later FULL render re-walks the points, and a finished
 * stroke asks for a full render.
 *
 * MEASURED, 2026-09-19, both numbers, on the machine in Sources and methods. The
 * entry improves and the pen gets worse, and the pen wins the argument.
 *
 * | Case                              | cache on | cache off |
 * | --------------------------------- | -------- | --------- |
 * | enter write mode, 20 pages, 20,000 | 2,576 ms | 2,136 ms  |
 * | enter write mode, 1 page, 5,000    | 1,864 ms | 1,160 ms  |
 * | pen-up to painted, 1,000 strokes   |  14.9 ms |   16.0 ms |
 * | pen-up to painted, 5,000 strokes   |  36.6 ms |   49.8 ms |
 *
 * Raw data: `section-a-tool-switch-real-base.{v2,cache-off}.json` and
 * `section-g-stroke-commit.{cache-on,cache-off,cache-on-dense,cache-off-dense}.json`.
 *
 * The keep rule was "the entry reaches its target AND the per-stroke commit stays
 * under 16 ms". Neither setting reaches the entry target, and turning the cache off
 * makes writing 36 percent slower on a dense page. A change that speeds up entering
 * a document and slows down using it is the wrong trade, so the cache stays on.
 *
 * Note what the last row also shows: the per-stroke commit is already 36.6 ms on a
 * page of 5,000 strokes WITH the cache. Every finished stroke asks for a full
 * render of the page. That is a separate defect and this flag does not address it.
 */
const INK_OBJECT_CACHING = true;

type Fabric = typeof import("fabric");
export type Sample = { x: number; y: number; pressure: number };
type SafariTouch = Touch & {
  touchType?: "direct" | "stylus";
};

/** The style of an ink stroke, recovered from a live Fabric object so an
 * area-erase can rebuild its surviving fragments identically. */
interface InkStyle {
  color: string;
  widthRel: number;
  opacity: number;
  pressure: boolean;
  /** Stroke half-width in seed px — folded into the eraser reach. */
  half: number;
}

/** The eraser's true-geometry test subject for one object, in SEED coordinates:
 * an ink polyline (with a half-width) or a filled rect (highlight / text / note). */
type SeedGeom =
  | { kind: "poly"; pts: Sample[]; half: number }
  | { kind: "rect"; rect: SeedRect };

/** One object's pending erase during a drag — held purely locally until the whole
 * drag commits on pointer-up (plan F2). `whole` hides the object and deletes it on
 * lift; `split` hides the original and previews the surviving spans, which commit
 * as a geometry UPDATE of the largest fragment plus CREATEs for the rest. */
type PendingErase =
  | { kind: "whole"; obj: any }
  | { kind: "split"; obj: any; runs: Sample[][]; style: InkStyle };

// Pointer ids are non-negative. These sentinels let the same stroke pipeline
// finish a WebKit fallback stream without manufacturing PointerEvent objects.
const TOUCH_FALLBACK_ID = -1;
const MOUSE_FALLBACK_ID = -2;
const COMPAT_MOUSE_WINDOW_MS = 1500;

// Cache the dynamic import so Fabric loads at most once, on first edit.
let fabricPromise: Promise<Fabric> | null = null;
export function loadFabric(): Promise<Fabric> {
  // Never cache a FAILED import: a transient chunk-load error (e.g. a network
  // blip when the reader-tab prefetch fires at idle, before the user even edits)
  // must not poison the promise so edit mode can never load for the rest of the
  // session — reset on failure so the next call retries.
  fabricPromise ??= import("fabric").catch((err) => {
    fabricPromise = null;
    throw err;
  });
  return fabricPromise;
}

const HIGHLIGHT_OPACITY = 0.3;

/** A pen signal that means "erase": the barrel/side button (buttons bit 2) or
 * the eraser end of the stylus (buttons bit 32, or button 5 on the transition).
 * Held for the duration of the press, so the pen erases while the button is
 * down and inks again the moment it is released. */
function penEraseSignal(e: PointerEvent): boolean {
  return e.pointerType === "pen" && ((e.buttons & 2) !== 0 || (e.buttons & 32) !== 0 || e.button === 5);
}

/** Clamp a raw PointerEvent.pressure to a usable [0.05, 1]. Mouse reports 0.5
 * while pressed; a 0 (touch with no force) becomes 0.5 so strokes never vanish. */
function clampPressure(raw: number | undefined): number {
  return Math.min(1, Math.max(0.05, raw || 0.5));
}

type Vec = { x: number; y: number };

/** Distance from point p to segment a→b. */
export function segPointDist(a: Vec, b: Vec, p: Vec): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  if (l2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Shortest distance between segments a→b and c→d (0 if they intersect). Used to
 * test the swept eraser segment against each ink segment. */
export function segSegDist(a: Vec, b: Vec, c: Vec, d: Vec): number {
  if (segSegIntersect(a, b, c, d)) return 0;
  return Math.min(
    segPointDist(a, b, c),
    segPointDist(a, b, d),
    segPointDist(c, d, a),
    segPointDist(c, d, b),
  );
}

function segSegIntersect(a: Vec, b: Vec, c: Vec, d: Vec): boolean {
  const o = (p: Vec, q: Vec, r: Vec) => Math.sign((q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y));
  const o1 = o(a, b, c);
  const o2 = o(a, b, d);
  const o3 = o(c, d, a);
  const o4 = o(c, d, b);
  return o1 !== o2 && o3 !== o4;
}

/** An axis-aligned rectangle in SEED space (highlight / text / note geometry). */
export interface SeedRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Shortest distance from segment a→b to the (filled) axis-aligned rectangle —
 * 0 if the segment touches or enters it. The eraser's TRUE-geometry test for a
 * highlight/text box: a capsule (segment + radius R) hits the rect exactly when
 * this distance ≤ R. This replaces the old inflated-bounding-box endpoint test
 * that "erased things it never touched" (plan F2). Pure + exported for tests. */
export function segRectDist(a: Vec, b: Vec, rect: SeedRect): number {
  const right = rect.left + rect.width;
  const bottom = rect.top + rect.height;
  const inside = (p: Vec) => p.x >= rect.left && p.x <= right && p.y >= rect.top && p.y <= bottom;
  // Either endpoint inside → the segment overlaps the filled rect → distance 0.
  if (inside(a) || inside(b)) return 0;
  const tl = { x: rect.left, y: rect.top };
  const tr = { x: right, y: rect.top };
  const br = { x: right, y: bottom };
  const bl = { x: rect.left, y: bottom };
  // Otherwise the nearest approach is to one of the four edges (segSegDist is 0
  // when the segment crosses an edge, so a chord through the rect reads as 0 too).
  return Math.min(
    segSegDist(a, b, tl, tr),
    segSegDist(a, b, tr, br),
    segSegDist(a, b, br, bl),
    segSegDist(a, b, bl, tl),
  );
}

/** Does the capsule (segment a→b, radius R) touch the filled rectangle? */
export function capsuleTouchesRect(a: Vec, b: Vec, R: number, rect: SeedRect): boolean {
  return segRectDist(a, b, rect) <= R;
}

/** Does the capsule (segment a→b, `reach` = R + stroke half-width) touch the
 * polyline through `pts`? A single point is treated as a dot. Pure — the eraser's
 * true-geometry test for ink, and the property test's subject. */
export function capsuleTouchesPolyline(a: Vec, b: Vec, reach: number, pts: Vec[]): boolean {
  if (pts.length === 0) return false;
  if (pts.length === 1) return segPointDist(a, b, pts[0]!) <= reach;
  for (let i = 1; i < pts.length; i++) {
    if (segSegDist(a, b, pts[i - 1]!, pts[i]!) <= reach) return true;
  }
  return false;
}

/** Cumulative polyline length of a run (seed px). */
export function runLength(run: { x: number; y: number }[]): number {
  let L = 0;
  for (let i = 1; i < run.length; i++) {
    L += Math.hypot(run[i]!.x - run[i - 1]!.x, run[i]!.y - run[i - 1]!.y);
  }
  return L;
}

/** Index of the LONGEST run — the fragment that keeps the original annotation id
 * on an area-split (only the extra fragments become new rows), so ids stop
 * churning and the undo/replay stay coherent (plan F2). */
export function largestRunIndex(runs: { x: number; y: number }[][]): number {
  let best = 0;
  let bestLen = -1;
  for (let i = 0; i < runs.length; i++) {
    const len = runLength(runs[i]!);
    if (len > bestLen) {
      bestLen = len;
      best = i;
    }
  }
  return best;
}

/** Map a viewport (client) coordinate into SEED-space scene coordinates given
 * the live upper-canvas rect. Rescaling the rect-relative offset by seed/rect
 * folds in BOTH the editor's own setZoom resize AND any ancestor CSS "world"
 * transform (the soft factor during a live pinch), so ink lands under the pen at
 * every zoom level — that is fix F3. Pure + exported for tests. */
export function sceneFromRect(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  seedW: number,
  seedH: number,
): { x: number; y: number } {
  const sx = rect.width > 0 ? seedW / rect.width : 1;
  const sy = rect.height > 0 ? seedH / rect.height : 1;
  return { x: (clientX - rect.left) * sx, y: (clientY - rect.top) * sy };
}

/** Map a viewport (client) coordinate into an INFINITE canvas's tile-space scene
 * coordinates by inverting the camera — Fabric's viewportTransform is [s,0,0,s,tx,ty],
 * so a scene point maps to screen `s·scene + t`; inverting gives `(client − rect −
 * t)/s`. Unbounded: negative and >tile coordinates are valid (that is the whole
 * point of an infinite canvas). Pure + exported for tests (fix F7 toScene). */
export function sceneFromCamera(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number },
  tx: number,
  ty: number,
  scale: number,
): { x: number; y: number } {
  const s = scale || 1;
  return { x: (clientX - rect.left - tx) / s, y: (clientY - rect.top - ty) / s };
}

/** The uniform 2D-context scale for the in-flight-stroke preview canvas: DPR ×
 * the live zoom (rendered width / seed width). Preview drawing is done in SEED
 * coordinates, so this makes it land correctly whatever size the page has been
 * resized to. Pure + exported for tests. */
/** The only 4 values of a text annotation that a renderer reads: the anchor, the
 *  string, and the size. `rect.w` and `rect.h` are persisted and read back by
 *  nothing, so a change in them is not a change worth a request. */
function textSig(
  x: number | undefined,
  y: number | undefined,
  text: string | undefined,
  rel: number | undefined,
): string {
  return `${x ?? ""}|${y ?? ""}|${text ?? ""}|${rel ?? ""}`;
}

export function previewCtxScale(renderedW: number, seedW: number, dpr: number): number {
  const zoom = seedW > 0 ? renderedW / seedW : 1;
  return dpr * zoom;
}

/** Wet-ink prediction (plan §3.1): extend the real stroke samples with the OS's
 * predicted continuation for the PREVIEW only. The committed array is never
 * touched — predictions must not enter what persists — and `out` is a caller-
 * owned reusable buffer so the per-move cost is a copy, not an allocation. Each
 * new batch of real samples rewrites the prediction set, so the preview always
 * shows samples + the LATEST prediction and nothing stale. Pure + exported for
 * tests. */
export function joinLivePreview(
  samples: ReadonlyArray<Sample>,
  predicted: ReadonlyArray<Sample>,
  out: Sample[],
): Sample[] {
  out.length = 0;
  for (const s of samples) out.push(s);
  for (const p of predicted) out.push(p);
  return out;
}

/** Camera-aware lasso crop for the INFINITE canvas (review item 8). There is no
 * tile-sized background canvas there — the only pixels are Fabric's viewport-
 * sized lower canvas, and lasso scene coords are unbounded (negative/far). So:
 * intersect the lasso's scene bbox with the scene rect the camera currently
 * shows, then map the intersection through the camera into lower-canvas DEVICE
 * pixels (screen = scene·s + t; device = screen × backing-store ratio). Returns
 * null when the visible intersection is empty or degenerate (< 8 device px on a
 * side) — a lasso fully outside the viewport captures nothing (fail-soft, same
 * as the paged path). Pure + exported for tests. */
export function lassoViewportCrop(
  bbox: { minX: number; minY: number; maxX: number; maxY: number },
  cam: { tx: number; ty: number; scale: number },
  view: { cssW: number; cssH: number; pxW: number; pxH: number },
): { sx: number; sy: number; sw: number; sh: number; scene: { x: number; y: number; w: number; h: number } } | null {
  const s = cam.scale || 1;
  // The visible scene rect: the camera inverse of the viewport corners.
  const minX = Math.max(bbox.minX, (0 - cam.tx) / s);
  const minY = Math.max(bbox.minY, (0 - cam.ty) / s);
  const maxX = Math.min(bbox.maxX, (view.cssW - cam.tx) / s);
  const maxY = Math.min(bbox.maxY, (view.cssH - cam.ty) / s);
  if (!(maxX > minX) || !(maxY > minY)) return null;
  // Scene → device px: through the camera, then the backing-store (retina) ratio.
  const kx = view.cssW > 0 ? view.pxW / view.cssW : 1;
  const ky = view.cssH > 0 ? view.pxH / view.cssH : 1;
  const sx = (minX * s + cam.tx) * kx;
  const sy = (minY * s + cam.ty) * ky;
  const sw = (maxX - minX) * s * kx;
  const sh = (maxY - minY) * s * ky;
  if (sw < 8 || sh < 8) return null;
  return { sx, sy, sw, sh, scene: { x: minX, y: minY, w: maxX - minX, h: maxY - minY } };
}

export class PageEditor {
  private canvas: any;
  private upper: HTMLElement;
  private pageEl: HTMLElement;
  private preview: HTMLCanvasElement;
  private previewCtx: CanvasRenderingContext2D | null;
  // The SEED page dimensions (px) captured at construction — the immutable
  // coordinate frame every Fabric object lives in FOREVER (the plan's W0/H0).
  // All normalize/denormalize math stays in seed units; zoom is realized by a
  // Fabric viewport transform (see resize), never by re-inflating objects.
  private W: number;
  private H: number;
  // INFINITE mode (plan F7): the editor's canvas fills the viewport and the camera
  // IS Fabric's viewportTransform. The seed frame W/H stays the TILE (unbounded
  // normalize), while these track the live camera so `toScene` can invert it and
  // the preview layer can follow it. Paged mode leaves `infinite` false untouched.
  private infinite = false;
  private camTx = 0;
  private camTy = 0;
  private camScale = 1;
  private tool: AnnotationTool = "select";
  private color: string;
  private font: string;
  private fontSizeRel: number;
  private strokeRel: number;
  private highlighterRel: number;
  private eraserRel: number;
  private eraseMode: EraseMode;
  private pressure: boolean;
  private smoothing: SmoothingPreset;
  // Shape styling for the next shape drawn (pushed from the toolbar like every
  // other tool field). Outline-only, solid, square corners by default — the
  // figure a shape has always been — so turning these on is always an addition.
  private shapeFill: string | null = null;
  private shapeDash = false;
  private shapeCorner = 0;
  // Eraser drag state (pointer-driven): active between an eraser-pointer down and
  // up so a drag sweeps strokes continuously. eraseId pins it to one pointer.
  /** Set the moment destroy() starts, so a callback that resolves after
   *  teardown (the font-load re-measure) does not touch a dead canvas. */
  /** The preview bitmap holds something a clear would need to remove. */
  private previewInk = false;
  private torn = false;
  /** applyTool has run at least once, so every object already carries the
   *  flags for the live tool. Until then the loop must run. */
  private toolApplied = false;
  private eraseActive = false;
  private eraseId: number | null = null;
  /** Whether the live erase is driven by a FINGER rather than a stylus or a
   *  mouse. A fingertip covers far more paper than a nib, and the ring has to
   *  match what the person is actually touching or the tool reads as unreliable
   *  — it misses, they swipe again, it misses again. */
  private eraseCoarse = false;
  private eraseLast: { x: number; y: number } | null = null;
  // Per-page broad-phase index (plan F2): every editable object's SEED-space bbox,
  // maintained incrementally as objects enter/leave/move, so an eraser sweep tests
  // only nearby candidates instead of rescanning every object each move.
  private readonly grid = new SpatialGrid();
  // The in-progress erase drag, held ENTIRELY locally (nothing hits the model or
  // network) until pointer-up: annId -> its pending disposition. On lift the whole
  // map commits as ONE transaction; on cancel (Esc / pointercancel) it's discarded.
  private pendingErase = new Map<string, PendingErase>();
  // Candidates the eraser is currently hovering over, faded to preview what a lift
  // would erase (OneNote's affordance); restored to their stored opacity the moment
  // the eraser moves away without committing to them.
  private eraseTint = new Map<string, { obj: any; opacity: number }>();
  // The one captured ink stroke (absolute canvas px); only this pointer draws.
  private activeStrokeId: number | null = null;
  private activePen = false;
  private strokeSamples: Sample[] = [];
  // Wet-ink prediction (plan §3.1): the OS-predicted continuation of the active
  // stroke, drawn on the PREVIEW only — never pushed into strokeSamples, so a
  // commit persists exactly what the pen really produced. Both arrays are
  // reused across moves (rewritten in place) to stay allocation-light.
  private readonly predicted: Sample[] = [];
  private readonly previewJoin: Sample[] = [];
  private lastPredLog = -1;
  // A shape being dragged out (rect / ellipse / diamond / line / arrow). The
  // REAL Fabric object is created on pointer-down and its geometry rewritten on
  // every move — no bespoke preview canvas — so what you drag is exactly what
  // commits. It carries no annId until pointer-up, so an abandoned drag leaves
  // nothing indexed and nothing persisted.
  private shapeDrag: { pointerId: number; from: Pt; obj: any | null } | null = null;
  /** An in-flight drag of a table's column/row boundary (select mode). */
  private tableDrag: {
    pointerId: number;
    obj: any;
    axis: "col" | "row";
    index: number;
    /** Whether the boundary actually moved — see endTableDrag. */
    moved: boolean;
  } | null = null;
  private activeTouchId: number | null = null;
  private lastPointerEnd = { at: 0, x: 0, y: 0 };
  // Direct-manipulation pointers may receive implicit capture before our
  // pointerdown listener. Pen capture is deliberately disabled in this build;
  // remember those ids so WebKit's delayed lost-capture echo is always inert.
  private uncapturedPenIds = new Set<number>();
  // Count of pending `lostpointercapture` echoes WE caused by calling
  // releasePointerCapture ourselves. Every release fires exactly one
  // lostpointercapture; on iPad the pen REUSES its pointerId across strokes, so
  // a delayed echo from the previous stroke's release would otherwise look
  // identical to — and wrongly finalize — the freshly-started next stroke (the
  // rapid lift-and-return drop). We swallow that many lost events before
  // treating one as a genuine, system-revoked capture loss.
  private selfReleaseLost = 0;
  // Finger navigation (one-finger pan + two-finger pinch/zoom) is owned by the
  // viewport-level gesture layer (use-viewport-gestures), NOT this per-page editor.
  private removeListeners?: () => void;

  constructor(
    private readonly fabric: Fabric,
    pageEl: HTMLElement,
    private readonly page: number,
    initial: Annotation[],
    private readonly handlers: PersistHandlers,
    opts: EditorOpts,
    // Present ⇒ INFINITE canvas (plan F7): the seed frame is the given TILE (used
    // for ALL normalize/denormalize math, unbounded), while the canvas ELEMENT fills
    // the host container and the camera rides Fabric's viewportTransform (setCamera).
    infinite?: { tileW: number; tileH: number },
  ) {
    this.pageEl = pageEl;
    this.color = opts.color;
    this.font = opts.font;
    this.fontSizeRel = opts.fontSizeRel;
    this.strokeRel = opts.strokeRel;
    this.highlighterRel = opts.highlighterRel;
    this.eraserRel = opts.eraserRel;
    this.eraseMode = opts.eraseMode;
    this.pressure = opts.pressure;
    this.smoothing = opts.smoothing;
    this.shapeFill = opts.shapeFill;
    this.shapeDash = opts.shapeDash;
    this.shapeCorner = opts.shapeCorner;
    this.infinite = !!infinite;
    // Seed frame: the TILE for an infinite canvas (unbounded scene coords), the
    // page's own pixel size for a paged one.
    this.W = infinite ? infinite.tileW : pageEl.clientWidth;
    this.H = infinite ? infinite.tileH : pageEl.clientHeight;
    // Canvas ELEMENT size: the container (infinite) or the page (paged).
    const cw = infinite ? pageEl.clientWidth : this.W;
    const ch = infinite ? pageEl.clientHeight : this.H;

    const el = document.createElement("canvas");
    el.width = cw;
    el.height = ch;
    el.classList.add("fabric-edit-layer");
    pageEl.appendChild(el);

    // enablePointerEvents → Fabric routes Pointer Events (so highlighter/text/
    // eraser get pen + touch + mouse). The PEN stroke is handled by our own
    // captured listeners below, not Fabric's brush.
    this.canvas = new this.fabric.Canvas(el, {
      selection: false,
      preserveObjectStacking: true,
      enablePointerEvents: true,
    });
    const wrap = this.canvas.wrapperEl as HTMLElement;
    wrap.style.position = "absolute";
    wrap.style.left = "0";
    wrap.style.top = "0";
    wrap.style.zIndex = "4";

    // A dedicated overlay canvas for the live in-flight stroke (above Fabric's
    // upper canvas, pointer-transparent so events still reach Fabric).
    const pv = document.createElement("canvas");
    pv.className = "fabric-preview-layer";
    pv.style.cssText = "position:absolute;left:0;top:0;z-index:5;pointer-events:none;";
    pageEl.appendChild(pv);
    this.preview = pv;
    // desynchronized (plan §3.1 wet-ink latency): lets the browser present this
    // canvas out of band with the DOM compositor, shaving a frame or two off the
    // pen→pixel path. A pure HINT — engines that don't support it ignore it, and
    // alpha behaviour is unchanged (default true, as before).
    this.previewCtx = pv.getContext("2d", { desynchronized: true });
    // Size the bitmap to the canvas ELEMENT (container for infinite, page for paged)
    // + set the ctx transform: the camera for infinite, DPR × zoom for paged.
    this.sizePreview(cw, ch);

    // The upper canvas owns the pen gesture (touch-action none → the browser
    // never hijacks an Apple Pencil stroke for scroll/zoom).
    const upper = this.canvas.upperCanvasEl as HTMLElement;
    this.upper = upper;
    upper.style.touchAction = "none";

    const onDown = (e: PointerEvent) => this.onPointerDown(e);
    const onMove = (e: PointerEvent) => this.onPointerMove(e);
    const onUp = (e: PointerEvent) => this.onPointerUp(e);
    const onLost = (e: PointerEvent) => this.onLostCapture(e);
    upper.addEventListener("pointerdown", onDown, true);
    upper.addEventListener("pointermove", onMove, true);
    upper.addEventListener("pointerup", onUp, true);
    upper.addEventListener("pointercancel", onUp, true);
    // The browser can revoke our pointer capture mid-stroke (the pen crossed out
    // of this page, a system gesture cut in). Catch it so the in-flight stroke
    // commits instead of stranding — otherwise the NEXT stroke is swallowed.
    upper.addEventListener("lostpointercapture", onLost, true);
    // WebKit can omit a rapid second PointerEvent stream on iPad while still
    // exposing the Apple Pencil through Touch Events or compatibility mouse
    // events. Pointer Events remain the primary path; these handlers activate
    // only when no pointer stroke exists, then feed the same sample/commit code.
    const onTouchStart = (e: TouchEvent) => this.onTouchStartFallback(e);
    const onTouchMove = (e: TouchEvent) => this.onTouchMoveFallback(e);
    const onTouchEnd = (e: TouchEvent) => this.onTouchEndFallback(e);
    const onMouseDown = (e: MouseEvent) => this.onMouseDownFallback(e);
    const onMouseMove = (e: MouseEvent) => this.onMouseMoveFallback(e);
    const onMouseUp = (e: MouseEvent) => this.onMouseUpFallback(e);
    const touchOpts = { passive: false, capture: true } as const;
    upper.addEventListener("touchstart", onTouchStart, touchOpts);
    upper.addEventListener("touchmove", onTouchMove, touchOpts);
    upper.addEventListener("touchend", onTouchEnd, touchOpts);
    upper.addEventListener("touchcancel", onTouchEnd, touchOpts);
    upper.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("mousemove", onMouseMove, true);
    document.addEventListener("mouseup", onMouseUp, true);
    // Esc mid-erase cancels the drag (restore everything, no transaction) — the
    // eraser's escape hatch. Document-level so it fires wherever focus sits.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && this.eraseActive) {
        this.cancelErase();
        return;
      }
      // Delete/Backspace removes the current selection (plan §3 item 3) as ONE
      // transaction. Guarded like the Ctrl+Z handler: only in select mode, never
      // while typing in a text box / input, and only for the on-screen editor — the
      // tool store is shared across reader tabs, so a hidden tab's editor (its host
      // display:none ⇒ upper.offsetParent null) must not also delete. Each page
      // editor listens; only the one holding a selection acts, the rest no-op.
      if ((e.key === "Delete" || e.key === "Backspace") && this.tool === "select") {
        if (this.upper.offsetParent === null) return;
        const active: any = this.canvas.getActiveObject();
        if (active?.isEditing) return; // editing text — the key belongs to its textarea
        const tgt = e.target as HTMLElement | null;
        if (tgt && (tgt.tagName === "INPUT" || tgt.tagName === "TEXTAREA" || tgt.isContentEditable)) return;
        if (this.deleteSelection()) e.preventDefault();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    this.removeListeners = () => {
      upper.removeEventListener("pointerdown", onDown, true);
      upper.removeEventListener("pointermove", onMove, true);
      upper.removeEventListener("pointerup", onUp, true);
      upper.removeEventListener("pointercancel", onUp, true);
      upper.removeEventListener("lostpointercapture", onLost, true);
      upper.removeEventListener("touchstart", onTouchStart, touchOpts);
      upper.removeEventListener("touchmove", onTouchMove, touchOpts);
      upper.removeEventListener("touchend", onTouchEnd, touchOpts);
      upper.removeEventListener("touchcancel", onTouchEnd, touchOpts);
      upper.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("mousemove", onMouseMove, true);
      document.removeEventListener("mouseup", onMouseUp, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };

    for (const a of initial) {
      try {
        const obj = this.toFabric(a);
        if (obj) {
          this.canvas.add(obj);
          this.indexObject(obj); // register its bbox for eraser broad-phase
        }
      } catch {
        /* skip a malformed annotation; never break the editor */
      }
    }
    this.sinkFills();

    this.wireEvents();
    this.applyTool(opts.tool);
    this.remeasureTextOnFontLoad();
  }

  /** Fabric measures an IText with `measureText` in its constructor and keeps
   *  the result in width, height and __charBounds. It re-measures only on a
   *  style edit. Five of the 6 annotation families load from Google Fonts with
   *  `display=swap`, and nothing in the studio awaits `document.fonts.ready`, so
   *  a cold mount measures the FALLBACK stack and keeps that number.
   *
   *  The glyph anchor survives, because originX and originY are left and top and
   *  the anchor comes from rect.x and rect.y. The selection box, the eraser bbox
   *  and the extent this editor persists do not. Re-measure once, when the real
   *  fonts land. */
  private remeasureTextOnFontLoad(): void {
    const fonts: any = (document as any).fonts;
    if (!fonts?.ready || fonts.status === "loaded") return;
    // Only a page that actually holds typed text has anything to re-measure. A page
    // of pure ink would otherwise walk every object once for nothing, and on a page
    // of 5,000 strokes that walk lands in the middle of the write-mode entry.
    let hasText = false;
    for (const obj of this.canvas.getObjects() as any[]) {
      if (obj.annType === "text" || obj.annType === "note") {
        hasText = true;
        break;
      }
    }
    if (!hasText) return;
    void Promise.resolve(fonts.ready)
      .then(() => {
        if (this.torn) return;
        let touched = false;
        for (const obj of this.canvas.getObjects() as any[]) {
          if (obj.annType !== "text" && obj.annType !== "note") continue;
          obj.initDimensions?.();
          obj.setCoords?.();
          this.indexObject(obj);
          touched = true;
        }
        if (touched) this.canvas.requestRenderAll();
      })
      .catch(() => {
        /* a font that never resolves must not break the editor */
      });
  }

  // -- geometry / sizing ----------------------------------------------------

  /** Resize the LIVING editor to a new page pixel size WITHOUT rebuilding it —
   * the P1 "editors are long-lived" invariant. Every Fabric object keeps its
   * seed-space [0..W0]×[0..H0] coordinates; a viewport zoom of w/W0 renders them
   * at the new size, so ink is NEVER re-inflated on zoom. The preview bitmap +
   * its ctx transform follow (so an in-flight stroke stays under the pen), and
   * the Fabric wrapper stays pinned at 0,0. */
  resize(w: number, h: number): void {
    if (!(w > 0) || !(h > 0)) return;
    if (this.infinite) {
      // Infinite: the canvas ELEMENT tracks the container; zoom is the camera's
      // (viewportTransform), NOT a setZoom of the element. setDimensions preserves
      // the viewportTransform, but re-assert it (and follow the preview) to be safe.
      this.canvas.setDimensions({ width: w, height: h });
      this.canvas.setViewportTransform([this.camScale, 0, 0, this.camScale, this.camTx, this.camTy]);
      this.canvas.requestRenderAll();
      this.sizePreview(w, h);
      if (this.activeStrokeId !== null && this.strokeSamples.length) this.drawPreview();
      else if (this.eraseActive && this.eraseLast) this.renderEraseFrame(this.eraseLast);
      return;
    }
    this.canvas.setDimensions({ width: w, height: h });
    this.canvas.setZoom(this.W > 0 ? w / this.W : 1);
    this.canvas.requestRenderAll();
    this.sizePreview(w, h);
    // sizePreview reassigns the preview bitmap, which clears it. Pen strokes
    // defer zoom commits (isPenStrokeActive) so they never resize mid-write, but
    // a finger/mouse stroke does NOT set that flag — a commit can land mid-stroke.
    // The samples live in seed coords (frame-invariant), so nothing is lost; just
    // repaint so the in-flight ink / eraser ring stays visible under the pointer.
    if (this.activeStrokeId !== null && this.strokeSamples.length) this.drawPreview();
    else if (this.eraseActive && this.eraseLast) this.renderEraseFrame(this.eraseLast);
  }

  /** Drive the INFINITE-canvas camera (plan F7): Fabric's viewportTransform IS the
   * camera, so [s,0,0,s,tx,ty] pans+zooms every object in place (never re-inflating
   * ink — the seed frame is fixed), and the preview layer's ctx transform follows so
   * an in-flight stroke / eraser ring stays under the pointer. The ViewportController's
   * onChange calls this every frame (cheap at canvas-note object counts). Paged mode
   * ignores it (the CSS world carries the camera there). */
  setCamera(tx: number, ty: number, scale: number): void {
    if (!this.infinite) return;
    this.camTx = tx;
    this.camTy = ty;
    this.camScale = scale;
    this.canvas.setViewportTransform([scale, 0, 0, scale, tx, ty]);
    this.applyCameraToPreview();
    this.canvas.requestRenderAll();
    if (this.activeStrokeId !== null && this.strokeSamples.length) this.drawPreview();
    else if (this.eraseActive && this.eraseLast) this.renderEraseFrame(this.eraseLast);
  }

  /** Set the infinite preview ctx transform to the camera (× DPR), so preview
   * drawing done in SEED/scene coords lands at screen `s·scene + t`, matching the
   * committed Fabric object. Re-applied after every bitmap resize (which clears it). */
  private applyCameraToPreview(): void {
    const dpr = window.devicePixelRatio || 1;
    const k = dpr * (this.camScale || 1);
    this.previewCtx?.setTransform(k, 0, 0, k, dpr * this.camTx, dpr * this.camTy);
  }

  /** Size the preview canvas bitmap to (w × h)·DPR and set its 2D transform so
   * preview drawing (done in SEED/scene coords) lands correctly after a resize:
   * DPR × zoom for a paged editor, the camera for an infinite one. Assigning
   * width/height resets the context, so the transform is (re)applied every time. */
  // NOTE: a resize reallocates the backing store, which blanks it, so previewInk
  // is reset there too.
  private sizePreview(w: number, h: number): void {
    const dpr = window.devicePixelRatio || 1;
    // Writing width or height reallocates the backing store, which blanks it.
    this.previewInk = false;
    this.preview.width = Math.max(1, Math.floor(w * dpr));
    this.preview.height = Math.max(1, Math.floor(h * dpr));
    this.preview.style.width = `${w}px`;
    this.preview.style.height = `${h}px`;
    if (this.infinite) {
      this.applyCameraToPreview();
      return;
    }
    const k = previewCtxScale(w, this.W, dpr);
    this.previewCtx?.setTransform(k, 0, 0, k, 0, 0);
  }

  /** The single pointer→scene mapping for every hand-rolled path (pen, eraser,
   * text, lasso): a client coord in SEED-space scene coordinates. Delegates to
   * sceneFromRect, which compensates both our setZoom AND the ancestor CSS world
   * factor, so drawing while soft-zoomed lands under the pen (fix F3). */
  private toScene(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.upper.getBoundingClientRect();
    if (this.infinite) {
      // Invert the camera into unbounded tile-space scene coords (fix F7). The rect
      // is the container-filling canvas, so `client − rect` is container-local; the
      // camera translation/scale then undoes the viewportTransform.
      return sceneFromCamera(clientX, clientY, rect, this.camTx, this.camTy, this.camScale);
    }
    return sceneFromRect(clientX, clientY, rect, this.W, this.H);
  }

  // -- input state machine (pen) --------------------------------------------

  /** The freehand tools that the hand-rolled stroke pipeline owns (pen + the
   * highlighter, which is just a broad, translucent, uniform pen). Everything
   * else (text / eraser / select) runs through Fabric. */
  private isInk(): boolean {
    // The lasso reuses the hand-rolled stroke pipeline to capture its path; it
    // just commits a region scan instead of ink (see finishStroke).
    return this.tool === "pen" || this.tool === "highlighter" || this.tool === "lasso";
  }

  /** Width (page-relative) of the active freehand tool. */
  private inkWidthRel(): number {
    return this.tool === "highlighter" ? this.highlighterRel : this.strokeRel;
  }

  private onPointerDown(e: PointerEvent): void {
    if (e.pointerType === "pen") markPen();
    // iPad WebKit can synthesize a text/page selection from rapid taps in
    // parallel with our pointer handling; drop any stray selection as each
    // tap/stroke begins so consecutive taps write instead of selecting. The
    // editor only mounts in edit mode, so this never runs in reading mode.
    window.getSelection?.()?.removeAllRanges();
    if (penDebugEnabled()) {
      penLog(`down ${fmtPtr(e)} active=${this.activeStrokeId ?? "-"} tool=${this.tool}`);
    }
    // ONE input policy (north-star B): a touch produces ink/erase ONLY when the
    // user opted into finger-drawing AND it isn't the writing hand's palm.
    // Otherwise a touch is navigation, owned by the scroller-level gesture layer
    // (which claimed the ones it wants in the capture phase) — never a tool action
    // here. Pen + mouse fall through. Reading the shared policy (not a private
    // hasSeenPen check) is what keeps the hook and the editor from ever disagreeing
    // about who owns a touch.
    if (e.pointerType === "touch" && !touchDrawAllowed(e)) {
      e.stopImmediatePropagation();
      return;
    }
    // Barrel/eraser button (or the eraser tool) → erase for the press duration.
    const erasing = penEraseSignal(e) || this.tool === "eraser";
    if (erasing) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.beginErase(e);
      return;
    }
    // Select mode over a table's grid line: drag the boundary, Google-Docs
    // style. Taken BEFORE Fabric sees the press, or Fabric would start moving
    // the whole object instead.
    if (this.tool === "select" && this.beginTableDrag(e)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    // The paint bucket acts on a TAP, not a drag: one click, one fill.
    if (this.tool === "fill") {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.fillAt(e);
      return;
    }
    // A shape is dragged out corner-to-corner. Like ink we own the gesture
    // outright (Fabric must not also target-find under the pointer), but the
    // geometry is authored directly on a real Fabric object rather than through
    // the sample/outline pipeline — shapes have no pressure and no latency
    // budget to fight for.
    if (isBoxShape(this.tool) || isSegmentShape(this.tool) || this.tool === "table") {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (this.infinite && !this.onPaper(e)) return;
      this.beginShape(e);
      return;
    }
    if (!this.isInk()) {
      // Text / select run through Fabric.
      return;
    }
    // ---- PEN / HIGHLIGHTER: we fully own the gesture ----
    e.preventDefault();
    e.stopImmediatePropagation(); // Fabric must not also handle the pen stroke
    // A previous stream whose end we never saw must be committed even when
    // WebKit reuses the same pointerId for the next Pencil contact.
    if (this.activeStrokeId !== null) {
      if (penDebugEnabled()) penLog("  finalize stranded stroke before new one");
      this.finalizeActiveStroke(false);
    }
    // The pen is deliberately NOT captured. On iPad, re-acquiring pointer
    // capture for the Pencil's REUSED pointerId on a fast lift-and-return left
    // the element unable to deliver `pointermove` for the new stroke (down + up
    // fired, moves didn't), so the letter committed empty. WebKit also assigns
    // implicit capture to a direct-manipulation pointer before our listener
    // runs; release it (and remember the id) so its delayed lostpointercapture
    // echo can't finalize the fresh stroke. Without capture, in-page writing is
    // delivered directly (touch-action:none), and the touch/mouse fallbacks
    // below recover the rare stream WebKit drops entirely.
    if (e.pointerType === "pen") {
      this.uncapturedPenIds.add(e.pointerId);
      try {
        if (this.upper.hasPointerCapture(e.pointerId)) {
          this.upper.releasePointerCapture(e.pointerId);
          if (penDebugEnabled()) penLog("  released implicit pen capture");
        }
      } catch {
        /* best effort; the fallback input paths still recover a missed stream */
      }
    }
    // The infinite canvas has a left edge (plan task 3): the grey beside the
    // paper is not writable, so a contact that STARTS there makes no mark at
    // all — the same as trying to write on the desk next to the pad.
    if (this.infinite && !this.onPaper(e)) {
      if (penDebugEnabled()) penLog("  ink-start refused: off the paper");
      return;
    }
    this.activeStrokeId = e.pointerId;
    this.activePen = e.pointerType === "pen";
    setPenStrokeActive(this.activePen);
    this.strokeSamples = [];
    this.predicted.length = 0;
    this.pushSample(e);
    this.drawPreview();
    if (penDebugEnabled()) penLog("  ink-start");
  }

  private onPointerMove(e: PointerEvent): void {
    if (e.pointerType === "pen") markPen();
    if (this.tableDrag && e.pointerId === this.tableDrag.pointerId) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.moveTableDrag(e);
      return;
    }
    // Hovering a table's grid line in select mode shows the resize cursor, so
    // the affordance is discoverable rather than something you find by accident.
    if (this.tool === "select" && !this.tableDrag) this.updateTableCursor(e);
    // Active shape drag — resize the in-flight object to the pointer.
    if (this.shapeDrag && e.pointerId === this.shapeDrag.pointerId) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.moveShape(e);
      return;
    }
    // Active eraser drag (pen tip, barrel button, or the eraser tool).
    if (this.eraseActive && e.pointerId === this.eraseId) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.moveErase(e);
      return;
    }
    // Finger navigation is the gesture layer's; a touch inks here only when it's
    // an allowed finger-draw (policy) OR it is the active stroke's own pointer.
    if (
      e.pointerType === "touch" &&
      !touchDrawAllowed(e) &&
      this.activeStrokeId !== e.pointerId
    ) {
      return;
    }
    // Eraser cursor: shown for the eraser tool, or while a pen's barrel button
    // is held (so the writer sees the hit radius before contact).
    const showEraser = this.tool === "eraser" || penEraseSignal(e);
    if (!this.isInk() || showEraser) {
      if (showEraser) this.drawEraserCursor(this.toScene(e.clientX, e.clientY));
      return;
    }
    if (
      this.activeStrokeId === null &&
      e.pointerType === "pen" &&
      ((e.buttons & 1) !== 0 || e.pressure > 0)
    ) {
      // Safari recovery: contact-bearing movement without a preceding
      // pointerdown. Starting at the first observable sample is preferable to
      // dropping the entire letter.
      e.preventDefault();
      e.stopImmediatePropagation();
      this.activeStrokeId = e.pointerId;
      this.activePen = true;
      this.strokeSamples = [];
      this.predicted.length = 0;
      this.pushSample(e);
      this.drawPreview();
      if (penDebugEnabled()) penLog(`  RECOVER pointermove ${fmtPtr(e)}`);
      return;
    }
    if (this.activeStrokeId !== e.pointerId) {
      // Hover (no active stroke) or a stray pointer — NEVER ink.
      return;
    }
    if ((e.buttons & 1) === 0 && e.pressure === 0) {
      if (penDebugEnabled()) penLog(`  RECOVER pointermove-release ${fmtPtr(e)}`);
      this.finalizeActiveStroke(false);
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    const evs = (e as any).getCoalescedEvents?.() ?? [e];
    for (const ev of evs) this.pushSample(ev);
    // Wet-ink prediction (plan §3.1): after the REAL samples, rewrite the
    // preview-only prediction set from the browser's predicted continuation —
    // each real batch drops the previous predictions, so nothing stale lingers.
    this.pushPredicted(e);
    this.drawPreview();
  }

  private onPointerUp(e: PointerEvent): void {
    if (this.tableDrag && e.pointerId === this.tableDrag.pointerId) {
      e.stopImmediatePropagation();
      this.endTableDrag(e.type !== "pointercancel");
      return;
    }
    if (this.shapeDrag && e.pointerId === this.shapeDrag.pointerId) {
      e.stopImmediatePropagation();
      // A cancel (system gesture cut in) abandons the shape rather than
      // committing a half-dragged one.
      this.endShape(e.type !== "pointercancel");
      return;
    }
    if (this.eraseActive && e.pointerId === this.eraseId) {
      // A real lift commits the drag; a pointercancel (system gesture cut in) is a
      // cancel — restore, don't half-erase.
      if (e.type === "pointercancel") this.cancelErase();
      else this.endErase();
      return;
    }
    if (this.activeStrokeId !== e.pointerId) {
      if (penDebugEnabled()) penLog(`${e.type} ${fmtPtr(e)} — no active match (${this.activeStrokeId ?? "-"})`);
      return;
    }
    e.stopImmediatePropagation();
    this.lastPointerEnd = { at: performance.now(), x: e.clientX, y: e.clientY };
    if (penDebugEnabled()) penLog(`${e.type} ${fmtPtr(e)} → finalize`);
    this.finalizeActiveStroke(true);
  }

  private onTouchStartFallback(e: TouchEvent): void {
    if (!this.isInk()) return;
    if (e.cancelable) e.preventDefault();
    const touch = this.firstStylusTouch(e.changedTouches);
    if (!touch || this.activeStrokeId !== null) return;
    e.stopImmediatePropagation();
    markPen();
    this.activeStrokeId = TOUCH_FALLBACK_ID;
    this.activeTouchId = touch.identifier;
    this.activePen = true;
    this.strokeSamples = [];
    this.predicted.length = 0;
    this.pushTouchSample(touch);
    this.drawPreview();
    if (penDebugEnabled()) penLog(`  RECOVER touchstart stylus#${touch.identifier}`);
  }

  private onTouchMoveFallback(e: TouchEvent): void {
    if (this.activeStrokeId !== TOUCH_FALLBACK_ID || this.activeTouchId === null) return;
    if (e.cancelable) e.preventDefault();
    e.stopImmediatePropagation();
    const touch = this.touchById(e.changedTouches, this.activeTouchId);
    if (!touch) return;
    this.pushTouchSample(touch);
    this.drawPreview();
  }

  private onTouchEndFallback(e: TouchEvent): void {
    if (this.activeStrokeId !== TOUCH_FALLBACK_ID || this.activeTouchId === null) return;
    const touch = this.touchById(e.changedTouches, this.activeTouchId);
    if (!touch) return;
    if (e.cancelable) e.preventDefault();
    e.stopImmediatePropagation();
    this.pushTouchSample(touch);
    if (penDebugEnabled()) penLog(`  RECOVER ${e.type} stylus#${touch.identifier}`);
    this.finalizeActiveStroke(false);
  }

  private onMouseDownFallback(e: MouseEvent): void {
    if (
      !this.isInk() ||
      e.button !== 0 ||
      this.activeStrokeId !== null ||
      !hasSeenPen() ||
      msSincePen() > COMPAT_MOUSE_WINDOW_MS
    ) {
      return;
    }
    // A normal PointerEvent stream may be followed by a compatibility mouse
    // event. Ignore that echo when it lands at the just-finished endpoint; the
    // missing rapid second iPad contact starts elsewhere and has no pointer pair.
    const echoAge = performance.now() - this.lastPointerEnd.at;
    const echoDistance = Math.hypot(
      e.clientX - this.lastPointerEnd.x,
      e.clientY - this.lastPointerEnd.y,
    );
    if (echoAge < 250 && echoDistance < 4) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.activeStrokeId = MOUSE_FALLBACK_ID;
    this.activePen = false;
    this.strokeSamples = [];
    this.predicted.length = 0;
    this.pushSample(e);
    this.drawPreview();
    if (penDebugEnabled()) penLog(`  RECOVER mousedown detail=${e.detail}`);
  }

  private onMouseMoveFallback(e: MouseEvent): void {
    if (this.activeStrokeId !== MOUSE_FALLBACK_ID) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if ((e.buttons & 1) === 0) {
      this.finalizeActiveStroke(false);
      return;
    }
    this.pushSample(e);
    this.drawPreview();
  }

  private onMouseUpFallback(e: MouseEvent): void {
    if (this.activeStrokeId !== MOUSE_FALLBACK_ID) {
      // WebKit may deliver the pointerdown/moves but end the stream with only a
      // compatibility mouseup. Finish that non-pen stroke instead of leaving
      // state stranded until the next contact.
      if (this.isInk() && this.activeStrokeId !== null && !this.activePen) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.pushSample(e);
        if (penDebugEnabled()) penLog("  RECOVER mouseup → finalize pointer stroke");
        this.finalizeActiveStroke(false);
      }
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    this.pushSample(e);
    if (penDebugEnabled()) penLog("  RECOVER mouseup");
    this.finalizeActiveStroke(false);
  }

  /** Capture revoked mid-stroke — the pen crossed off this page, or a system
   * gesture cut in. Commit what we have so a quick out-and-back never loses the
   * stroke (and never strands state that swallows the next one). Guarded by
   * pointerId: the lostpointercapture that normally follows every pointerup is a
   * no-op here (activeStrokeId is already cleared), and a delayed loss for an
   * OLD pointer can't clobber a stroke that already started. */
  private onLostCapture(e: PointerEvent): void {
    if (e.pointerType === "pen" && this.uncapturedPenIds.has(e.pointerId)) {
      if (penDebugEnabled()) penLog(`lostcapture ${fmtPtr(e)} — uncaptured pen, ignored`);
      return;
    }
    // First, absorb an echo from a release WE issued (normal pointerup / pan
    // end). With the pen's reused pointerId this is the ONLY way to tell our own
    // release's echo from a real mid-stroke revocation — without it, a stale
    // echo from the previous stroke kills the next one on a fast re-tap.
    if (this.selfReleaseLost > 0) {
      this.selfReleaseLost--;
      if (penDebugEnabled()) penLog(`lostcapture ${fmtPtr(e)} — self-release, ignored`);
      return;
    }
    if (this.activeStrokeId !== e.pointerId) return;
    if (penDebugEnabled()) penLog(`lostcapture ${fmtPtr(e)} → finalize (revoked)`);
    this.finalizeActiveStroke(false);
  }

  /** End the active pen stroke: clear live state, optionally release capture,
   * and commit it. Idempotent (guarded by activeStrokeId) so a normal
   * pointerup, a lostpointercapture, and the stranded-stroke cleanup on the next
   * pointerdown can all call it without double-committing. State is cleared
   * BEFORE committing so a re-entrant event during commit sees a clean slate. */
  private finalizeActiveStroke(release: boolean): void {
    const id = this.activeStrokeId;
    if (id === null) return;
    const samples = this.strokeSamples;
    const wasPen = this.activePen;
    this.activeStrokeId = null;
    this.activeTouchId = null;
    this.strokeSamples = [];
    // Predictions are preview-only: drop them so the commit below (and the next
    // stroke's first preview frame) can never see a predicted point (§3.1).
    this.predicted.length = 0;
    setPenStrokeActive(false);
    if (release && id >= 0) {
      try {
        if (this.upper.hasPointerCapture(id)) {
          this.upper.releasePointerCapture(id);
          // Releasing fires one lostpointercapture; mark it ours to swallow.
          this.selfReleaseLost++;
        }
      } catch {
        /* ignore — no capture held, so no echo will come */
      }
    }
    this.clearPreview();
    this.finishStroke(samples, wasPen);
  }

  /** Replay a stroke the pen-down buffer captured during the first-edit dead zone
   * (F1.3), before this editor existed. Client samples are mapped into this
   * editor's SEED frame with the same toScene() the live input uses, then fed
   * through the EXISTING commit pipeline (finishStroke) — no forked ink math.
   * `live` adopts a still-in-flight stroke so it previews and this editor's own
   * pointer handlers carry it to commit; otherwise it commits straight away. */
  ingestBufferedStroke(
    samples: BufferedSample[],
    opts: { pointerId: number; pen: boolean; live: boolean },
  ): void {
    if (samples.length < 1) return;
    // The tool can change during the dead zone (buffered with pen, then the user
    // taps eraser/text/lasso before the editors attach). Only replay into a
    // freehand-ink tool — mirroring the buffer's own isInkTool capture gate — so
    // a stale contact never commits ink under a non-ink tool or triggers a stray
    // lasso region scan. Dropping the late mark surprises less than either.
    if (this.tool !== "pen" && this.tool !== "highlighter") return;
    const seeded: Sample[] = samples.map((s) => {
      const p = this.toScene(s.clientX, s.clientY);
      return { x: p.x, y: p.y, pressure: clampPressure(s.pressure) };
    });
    if (opts.pen) markPen();
    if (opts.live) {
      // Adopt as the active stroke: preview it now and let onPointerMove/onPointerUp
      // finish it (the buffer's own up backstops the finalize — see PenDownBuffer).
      if (this.activeStrokeId !== null) this.finalizeActiveStroke(false);
      this.activeStrokeId = opts.pointerId;
      this.activePen = opts.pen;
      this.strokeSamples = seeded;
      this.predicted.length = 0;
      setPenStrokeActive(opts.pen);
      this.drawPreview();
      if (penDebugEnabled()) penLog(`  ingest live #${opts.pointerId} (${seeded.length} samp)`);
      return;
    }
    if (penDebugEnabled()) penLog(`  ingest commit #${opts.pointerId} (${seeded.length} samp)`);
    this.finishStroke(seeded, opts.pen);
    this.canvas.requestRenderAll();
  }

  /** Backstop for a live-handed-off buffered stroke: finalize it if this editor
   * still owns the pointer (its own handlers never saw the up), else a no-op. */
  finalizeBufferedStroke(pointerId: number): void {
    if (this.activeStrokeId === pointerId) this.finalizeActiveStroke(true);
  }

  // -- eraser (pointer-driven, geometric) -----------------------------------

  /** Current eraser hit radius in canvas px (a generous minimum so a quick swipe
   * reliably catches thin strokes — the old Fabric tolerance missed most). */
  // -- shapes: rect / ellipse / diamond / line / arrow ------------------------

  /** Begin dragging a shape out from the pointer-down point. The object is real
   * from the first frame (Fabric renders it), so the drag preview and the
   * committed shape are the same geometry by construction. */
  private beginShape(e: PointerEvent): void {
    // A stranded drag (its up never arrived) must not leave a ghost behind.
    if (this.shapeDrag) this.endShape(false);
    const raw = this.toScene(e.clientX, e.clientY);
    const from = this.infinite ? { x: clampWritableX(raw.x), y: clampWritableY(raw.y) } : raw;
    // A zero-size figure may legitimately have nothing to draw yet — a
    // terminator's radius starts at 0, so its path is empty — so the drag
    // begins WITHOUT an object and the first move builds one. Requiring one up
    // front silently made every curved shape undraggable.
    const obj = this.shapeObject(this.tool as BoxShape | SegmentShape | "table", from, from);
    if (obj) this.canvas.add(obj);
    this.shapeDrag = { pointerId: e.pointerId, from, obj };
    this.canvas.requestRenderAll();
  }

  private moveShape(e: PointerEvent): void {
    const drag = this.shapeDrag;
    if (!drag) return;
    const rawTo = this.toScene(e.clientX, e.clientY);
    const to = this.infinite ? { x: clampWritableX(rawTo.x), y: clampWritableY(rawTo.y) } : rawTo;
    if (drag.obj) this.canvas.remove(drag.obj);
    const next = this.shapeObject(this.tool as BoxShape | SegmentShape | "table", drag.from, to);
    if (!next) return;
    drag.obj = next;
    this.canvas.add(next);
    this.canvas.requestRenderAll();
  }

  /** Finish a shape drag. `commit` false (a pointercancel, or a tool switch
   * mid-drag) throws it away. A drag too small to be intentional is also
   * dropped — a tap with the rectangle tool should not leave an invisible
   * zero-size annotation that only the eraser can find. */
  private endShape(commit: boolean): void {
    const drag = this.shapeDrag;
    this.shapeDrag = null;
    if (!drag) return;
    const draft = commit && drag.obj ? this.shapeDraft(drag.obj) : null;
    if (!draft) {
      if (drag.obj) this.canvas.remove(drag.obj);
      this.canvas.requestRenderAll();
      return;
    }
    this.create(drag.obj, draft); // tags the temp id and indexes it for the eraser
    this.canvas.requestRenderAll();
  }

  /** A shape's Fabric object for the drag from `from` to `to`. Box shapes
   * normalize the drag to a top-left box (dragging up-left is the same
   * rectangle); segments keep their true direction so an arrow points where it
   * was drawn. */
  private shapeObject(kind: BoxShape | SegmentShape | "table", from: Pt, to: Pt): any {
    const width = shapeStrokeWidth(this.strokeRel, this.W);
    const common = {
      stroke: this.color,
      strokeWidth: width,
      fill: "",
      strokeUniform: true,
      strokeLineJoin: "round" as const,
      strokeLineCap: "round" as const,
      originX: "left" as const,
      originY: "top" as const,
      lockRotation: true,
      objectCaching: false,
      selectable: this.tool === "select",
      evented: this.tool === "select",
    };
    let obj: any;
    if (kind === "table") {
      const box = boxFromDrag(from, to);
      const width = shapeStrokeWidth(this.strokeRel, this.W);
      const { cols, rows } = tableTracksForBox(box, width, this.W);
      const tCols = evenTracks(cols);
      const tRows = evenTracks(rows);
      const d = tablePath(box, tCols, tRows);
      if (!d) return null;
      const t: any = new this.fabric.Path(d, { ...common, fill: "" });
      t.annType = "table";
      t.annBox = box;
      t.annCols = tCols;
      t.annRows = tRows;
      t.annStrokeRel = this.strokeRel;
      return t;
    }
    if (isSegmentShape(kind)) {
      // An arrow is the shaft plus its two barbs in ONE polyline path, so the
      // head can never drift away from the line it belongs to (and a select-mode
      // move carries both). `arrowHead` is shared with the overlay and mirrored
      // by the PDF flatten, so all three draw the same head.
      const pts =
        kind === "arrow"
          ? (() => {
              const barbs = arrowHead(from, to, width);
              // Trace out to the tip, down one barb, back to the tip, out the
              // other: a single stroke that renders as a closed arrow head.
              return barbs.length === 2
                ? [from, to, barbs[0]!, to, barbs[1]!]
                : [from, to];
            })()
          : [from, to];
      obj = new this.fabric.Polyline(pts, common);
    } else {
      const box = boxFromDrag(from, to);
      if (kind === "rect" && this.shapeCorner > 0) {
        const d = boxShapePath("rect", box, this.shapeCorner);
        obj = d ? new this.fabric.Path(d, { ...common }) : null;
        if (!obj) return null;
      } else if (kind === "rect") {
        obj = new this.fabric.Rect({ ...common, left: box.x, top: box.y, width: box.w, height: box.h });
      } else if (kind === "ellipse") {
        const { rx, ry } = ellipseParams(box);
        obj = new this.fabric.Ellipse({ ...common, left: box.x, top: box.y, rx, ry });
      } else if (kind === "diamond") {
        obj = new this.fabric.Polygon(diamondPoints(box), common);
      } else {
        // Parallelogram / terminator / hexagon / cylinder — drawn from the same
        // path string the SVG overlay and the PDF renderers use, so a flowchart
        // shape is one figure defined in one place rather than four.
        const d = boxShapePath(kind as BoxShape, box);
        if (!d) return null;
        obj = new this.fabric.Path(d, { ...common, fill: "" });
      }
    }
    obj.annType = kind;
    obj.annStrokeRel = this.strokeRel;
    obj.annFill = this.shapeFill;
    obj.annDash = this.shapeDash;
    obj.annCorner = this.shapeCorner;
    obj.set({
      fill: this.shapeFill || "",
      strokeDashArray: this.shapeDash ? dashPattern(shapeStrokeWidth(this.strokeRel, this.W)) : null,
    });
    // Segment shapes remember their true endpoints: a Polyline's own points are
    // rebased to its bounding box, so the arrow's direction (and which end the
    // head is on) could not be recovered from them alone.
    if (isSegmentShape(kind)) obj.annSegment = [from, to];
    if (isBoxShape(kind)) obj.annBox = boxFromDrag(from, to);
    return obj;
  }

  /** The draft for a freshly-dragged shape, or null when the drag was too small
   * to be meant. The threshold is in seed px and scaled by the camera so it is
   * the same physical flick at any zoom. */
  private shapeDraft(obj: any): AnnotationDraft | null {
    const MIN = 4 / (this.infinite ? this.camScale : 1);
    const base = {
      page: this.page,
      color: this.color,
      stroke_rel: this.strokeRel,
      ...(this.shapeFill ? { fill_color: this.shapeFill } : {}),
      ...(this.shapeDash ? { dash: true } : {}),
      ...(this.shapeCorner > 0 ? { corner_radius: this.shapeCorner } : {}),
    } satisfies Partial<AnnotationDraft> & { page: number };
    if (obj.annSegment) {
      const [a, b] = obj.annSegment as [Pt, Pt];
      if (Math.hypot(b.x - a.x, b.y - a.y) < MIN) return null;
      return {
        ...base,
        type: obj.annType,
        points: [
          { x: a.x / this.W, y: a.y / this.H },
          { x: b.x / this.W, y: b.y / this.H },
        ],
      };
    }
    const box = obj.annBox as AbsBox | undefined;
    if (!box || (box.w < MIN && box.h < MIN)) return null;
    const rect = { x: box.x / this.W, y: box.y / this.H, w: box.w / this.W, h: box.h / this.H };
    if (obj.annType === "table") {
      return { ...base, type: "table", rect, cols: obj.annCols, rows: obj.annRows };
    }
    return { ...base, type: obj.annType, rect };
  }

  /** Rebuild a saved shape at the page's current pixel size. */
  private shapeFromAnnotation(a: Annotation): any {
    if (isSegmentShape(a.type)) {
      const seg = absSegment(a, this.W, this.H);
      if (!seg) return null;
      const obj = this.shapeObjectFromGeometry(a, seg[0], seg[1]);
      return obj;
    }
    const box = absBox(a, this.W, this.H);
    if (!box) return null;
    return this.shapeObjectFromGeometry(
      a,
      { x: box.x, y: box.y },
      { x: box.x + box.w, y: box.y + box.h },
    );
  }

  /** Shared by load and drag so a reloaded shape is byte-identical to the one
   * you drew: same builder, only the style comes from the annotation. */
  private shapeObjectFromGeometry(a: Annotation, from: Pt, to: Pt): any {
    const color = this.color;
    const strokeRel = this.strokeRel;
    const fill = this.shapeFill;
    const dash = this.shapeDash;
    const corner = this.shapeCorner;
    // Borrow the live-tool builder with the annotation's OWN style swapped in —
    // a saved shape must not adopt whatever the toolbar happens to be set to.
    this.color = a.color ?? "#111111";
    this.strokeRel = a.stroke_rel ?? 0.004;
    this.shapeFill = a.fill_color ?? null;
    this.shapeDash = !!a.dash;
    this.shapeCorner = a.corner_radius ?? 0;
    try {
      const obj = this.shapeObject(a.type as BoxShape | SegmentShape, from, to);
      if (obj) {
        obj.annId = a.id;
        obj.annOpacity = a.opacity ?? 1;
        if (a.opacity != null && a.opacity < 1) obj.set({ opacity: a.opacity });
        obj.annFill = a.fill_color ?? null;
        obj.annDash = !!a.dash;
        obj.annCorner = a.corner_radius ?? 0;
        this.applyShapeStyle(obj);
      }
      return obj;
    } finally {
      this.color = color;
      this.strokeRel = strokeRel;
      this.shapeFill = fill;
      this.shapeDash = dash;
      this.shapeCorner = corner;
    }
  }

  /** The table under the pointer whose grid line is within grabbing distance,
   * with which line it is. Tolerance scales with the stroke so a heavy table is
   * no harder to grab than a fine one, and with the camera so it is a constant
   * distance on SCREEN at any zoom. */
  private tableLineUnder(
    e: { clientX: number; clientY: number },
  ): { obj: any; axis: "col" | "row"; index: number } | null {
    const p = this.toScene(e.clientX, e.clientY);
    const zoom = this.infinite ? this.camScale : 1;
    for (const obj of this.canvas.getObjects()) {
      const o = obj as any;
      if (o.annType !== "table" || !o.annBox) continue;
      const tol = Math.max((o.strokeWidth ?? 2) * 1.5, 8 / (zoom || 1));
      const hit = tableLineAt(o.annBox, o.annCols, o.annRows, p, tol);
      if (hit) return { obj: o, ...hit };
    }
    return null;
  }

  private updateTableCursor(e: PointerEvent): void {
    const hit = this.tableLineUnder(e);
    const want = hit ? (hit.axis === "col" ? "col-resize" : "row-resize") : "default";
    if (this.canvas.defaultCursor !== want) {
      this.canvas.defaultCursor = want;
      this.upper.style.cursor = want;
    }
  }

  /** Start dragging a table boundary. Returns whether one was grabbed. */
  private beginTableDrag(e: PointerEvent): boolean {
    const hit = this.tableLineUnder(e);
    if (!hit) return false;
    // Take the object out of Fabric's hands for the duration, so the drag moves
    // the line and not the table.
    this.canvas.discardActiveObject();
    this.tableDrag = {
      pointerId: e.pointerId,
      obj: hit.obj,
      axis: hit.axis,
      index: hit.index,
      moved: false,
    };
    return true;
  }

  private moveTableDrag(e: PointerEvent): void {
    const drag = this.tableDrag;
    if (!drag) return;
    const p = this.toScene(e.clientX, e.clientY);
    const box = drag.obj.annBox as AbsBox;
    const before = drag.axis === "col" ? drag.obj.annCols : drag.obj.annRows;
    const after =
      drag.axis === "col"
        ? resizeTrack(before, drag.index, box.x, box.w, p.x)
        : resizeTrack(before, drag.index, box.y, box.h, p.y);
    if (after === before) return;
    if (drag.axis === "col") drag.obj.annCols = after;
    else drag.obj.annRows = after;
    drag.moved = true;
    this.repaintTable(drag.obj);
    this.canvas.requestRenderAll();
  }

  /** Finish the drag and persist the new tracks — one update, on release, not
   * one per pointermove. */
  private endTableDrag(commit: boolean): void {
    const drag = this.tableDrag;
    this.tableDrag = null;
    if (!drag) return;
    // A press that grabbed a line but never moved it is not an edit; writing
    // the unchanged tracks would be pure noise on the wire and in the undo stack.
    if (commit && drag.moved && drag.obj.annId) {
      this.persistUpdate(drag.obj.annId, {
        cols: drag.obj.annCols,
        rows: drag.obj.annRows,
      });
      this.indexObject(drag.obj);
    }
    this.canvas.requestRenderAll();
  }

  /** Rebuild a saved table at the page's current pixel size. */
  private tableFromAnnotation(a: Annotation): any {
    const box = absBox(a, this.W, this.H);
    if (!box) return null;
    const src = a as unknown as { cols?: number[] | null; rows?: number[] | null };
    const cols = normalizeTracks(src.cols);
    const rows = normalizeTracks(src.rows);
    const d = tablePath(box, cols, rows);
    if (!d) return null;
    const obj: any = new this.fabric.Path(d, {
      stroke: a.color ?? "#111111",
      strokeWidth: shapeStrokeWidth(a.stroke_rel, this.W),
      fill: "",
      strokeUniform: true,
      strokeLineJoin: "round",
      strokeLineCap: "round",
      originX: "left",
      originY: "top",
      lockRotation: true,
      objectCaching: false,
      selectable: this.tool === "select",
      evented: this.tool === "select",
    });
    obj.annId = a.id;
    obj.annType = "table";
    obj.annBox = box;
    obj.annCols = cols;
    obj.annRows = rows;
    obj.annStrokeRel = a.stroke_rel ?? 0.004;
    return obj;
  }

  /** Redraw a table object's path from its CURRENT box + tracks, in place. */
  private repaintTable(obj: any): void {
    const d = tablePath(obj.annBox, obj.annCols, obj.annRows);
    if (!d) return;
    const fresh: any = new this.fabric.Path(d, {});
    obj.set({
      path: fresh.path,
      left: obj.annBox.x,
      top: obj.annBox.y,
      width: obj.annBox.w,
      height: obj.annBox.h,
      pathOffset: { x: obj.annBox.x + obj.annBox.w / 2, y: obj.annBox.y + obj.annBox.h / 2 },
    });
    obj.setCoords?.();
  }

  /** A shape's outline in SEED coordinates — what the eraser hit-tests and the
   * spatial grid keys on. Deliberately the OUTLINE, not the filled box: a shape
   * is drawn as a line, so the eraser must catch the line, not the empty middle
   * of a big rectangle. */
  private shapeSeedGeom(obj: any): SeedGeom | null {
    const half = Math.max(1, (obj.strokeWidth ?? 2) / 2);
    const pts = this.shapeAbsPoints(obj);
    if (!pts || pts.length < 2) return null;
    return { kind: "poly", pts: pts.map((p) => ({ x: p.x, y: p.y, pressure: 0.5 })), half };
  }

  /** A shape's outline points in canvas coordinates, folding in any select-mode
   * move/resize (which Fabric expresses as a transform, not as new points). */
  private shapeAbsPoints(obj: any): Pt[] | null {
    const m = obj.calcTransformMatrix();
    const off = obj.pathOffset ?? { x: 0, y: 0 };
    const map = (p: Pt): Pt => {
      const abs = this.fabric.util.transformPoint(new this.fabric.Point(p.x - off.x, p.y - off.y), m);
      return { x: abs.x, y: abs.y };
    };
    if (obj.annSegment) return (obj.points as Pt[]).map(map);
    const box = this.shapeBoxOf(obj);
    if (!box) return null;
    if (obj.annType === "rect") return outlinePoints("rect", box);
    if (obj.annType === "diamond") return outlinePoints("diamond", box);
    return outlinePoints("ellipse", box);
  }

  /** A box shape's CURRENT bounding box in canvas coords (its drawn box, not the
   * stroke-inflated render bounds), including any select-mode transform. */
  private shapeBoxOf(obj: any): AbsBox | null {
    const sx = Math.abs(obj.scaleX ?? 1);
    const sy = Math.abs(obj.scaleY ?? 1);
    let left = obj.left ?? 0;
    let top = obj.top ?? 0;
    let w = (obj.width ?? 0) * sx;
    let h = (obj.height ?? 0) * sy;
    if (obj.group) {
      // Inside a marquee selection Fabric nests coords in the group transform;
      // the scene-plane bounding rect is the honest absolute geometry. It
      // includes the stroke, so pull the half-width back off each edge.
      const br = obj.getBoundingRect();
      const inset = (obj.strokeWidth ?? 0) / 2;
      left = br.left + inset;
      top = br.top + inset;
      w = Math.max(0, br.width - 2 * inset);
      h = Math.max(0, br.height - 2 * inset);
    }
    if (!(w >= 0) || !(h >= 0)) return null;
    return { x: left, y: top, w, h };
  }

  /** Paint-bucket: flood the area under the pointer and persist it as polygon
   * rings. The raster is a means, never the product — see flood-fill.ts.
   *
   * The pixels come from Fabric's own lower canvas, which holds exactly the
   * markup and nothing else: the paper (colour, rules, grid) is CSS behind it,
   * so a fill spreads across ruled lines the way a person expects and is bounded
   * only by things that were actually drawn. */
  private fillAt(e: PointerEvent): void {
    const src = this.canvas.lowerCanvasEl as HTMLCanvasElement | null;
    const rect = this.upper.getBoundingClientRect();
    if (!src || !(src.width > 0) || !(rect.width > 0)) return;
    // Work at CSS resolution, capped: a fill is a per-click operation over the
    // whole surface, and a retina page is 4x the pixels for no visible gain.
    const scale = Math.min(1, FILL_MAX_DIM / Math.max(rect.width, rect.height));
    const w = Math.max(1, Math.round(rect.width * scale));
    const h = Math.max(1, Math.round(rect.height * scale));
    let image: ImageData;
    try {
      const off = document.createElement("canvas");
      off.width = w;
      off.height = h;
      const ctx = off.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(src, 0, 0, w, h);
      image = ctx.getImageData(0, 0, w, h);
    } catch {
      return; // a tainted/unavailable canvas: fail soft, never break writing
    }
    const seed: FillPoint = {
      x: (e.clientX - rect.left) * scale,
      y: (e.clientY - rect.top) * scale,
    };
    const out = computeFill(image.data, w, h, seed, { gap: Math.max(1, Math.round(3 * scale)) });
    if (!out.ok) {
      this.handlers.onFillRefused?.(out.reason as FillFailure);
      return;
    }
    // Mask pixels -> scene coords, through the SAME mapping a pointer takes, so
    // a fill lands exactly where it was flooded at any zoom or camera position.
    const toSceneFromMask = (p: FillPoint) =>
      this.toScene(rect.left + p.x / scale, rect.top + p.y / scale);
    const rings = out.rings.map((ring) =>
      ring.map((p) => {
        const s = toSceneFromMask(p);
        return { x: s.x / this.W, y: s.y / this.H };
      }),
    );
    const obj = this.fillObject(rings);
    if (!obj) return;
    // Behind everything: the fill meets the ink but must never sit on top of it.
    this.canvas.add(obj);
    obj.sendToBack?.() ?? this.canvas.sendObjectToBack?.(obj);
    this.create(obj, { page: this.page, type: "fill", color: this.color, rings });
    this.canvas.requestRenderAll();
  }

  /** A saved fill as a Fabric object: one even-odd path over all its rings. */
  private fillObject(rings: { x: number; y: number }[][], color = this.color, opacity = 1): any {
    const d = rings
      .filter((r) => r.length >= 3)
      .map((r) => `M ${r.map((p) => `${p.x * this.W} ${p.y * this.H}`).join(" L ")} Z`)
      .join(" ");
    if (!d) return null;
    const obj: any = new this.fabric.Path(d, {
      fill: color,
      stroke: "",
      opacity,
      fillRule: "evenodd",
      originX: "left",
      originY: "top",
      objectCaching: false,
      lockRotation: true,
      selectable: this.tool === "select",
      evented: this.tool === "select",
    });
    obj.annType = "fill";
    obj.annRings = rings;
    obj.annOpacity = opacity;
    return obj;
  }

  /** Push every fill behind every other object — see `inPaintOrder` in
   * paint-order.ts for the rule and why it exists.
   *
   * `fillAt` already sends a FRESHLY painted fill to the back, but every path
   * that rebuilds objects from stored annotations (initial load, reconcile,
   * undo/redo, an external add) adds them in list order, so a fill saved after a
   * stroke came back on top of it. Over a PDF that reads as the whole page
   * turning black: the page raster is a layer BELOW this canvas, so a page-sized
   * fill hides the document itself.
   *
   * Walked in REVERSE because each send-to-back lands at index 0; reversing
   * keeps the fills' relative order, so two overlapping fills stack the way they
   * were painted. Cheap and idempotent — safe to call after any bulk add. */
  private sinkFills(): void {
    const objs: any[] = this.canvas.getObjects?.() ?? [];
    const fills = objs.filter((o) => o?.annType === "fill");
    if (!fills.length || fills.length === objs.length) return;
    for (let i = fills.length - 1; i >= 0; i--) {
      const f = fills[i];
      if (typeof f.sendToBack === "function") f.sendToBack();
      else this.canvas.sendObjectToBack?.(f);
    }
  }

  /** Is this pointer over the writable paper? The infinite canvas has a top-left
   * corner (task 3): the grey outside it is not a surface you can mark. */
  private onPaper(e: { clientX: number; clientY: number }): boolean {
    const p = this.toScene(e.clientX, e.clientY);
    return isWritablePoint(p.x, p.y);
  }

  /** The eraser ring, in scene px.
   *
   * The floor is per-contact. 8px is a sane minimum for a nib or a cursor and
   * far too small for a fingertip, whose real contact patch is roughly 40px
   * across: at tablet zoom `eraserRel * W` can land near that floor, and an
   * 8px ring under a 40px finger misses almost everything it looks like it is
   * touching. The ring is DRAWN at this radius too, so what you see is what
   * erases either way. */
  private eraserRadiusPx(): number {
    const floor = this.eraseCoarse ? 22 : 8;
    return Math.max(floor, this.eraserRel * this.W);
  }

  private beginErase(e: PointerEvent): void {
    this.eraseActive = true;
    this.eraseId = e.pointerId;
    this.eraseCoarse = e.pointerType === "touch";
    const p = this.toScene(e.clientX, e.clientY);
    this.eraseLast = p;
    this.eraseSweep(p, p); // a tap-erase catches whatever is under the ring
    this.updateTints(p);
    this.canvas.requestRenderAll();
    this.renderEraseFrame(p);
  }

  private moveErase(e: PointerEvent): void {
    const evs = (e as any).getCoalescedEvents?.() ?? [e];
    let from = this.eraseLast ?? this.toScene(e.clientX, e.clientY);
    for (const ev of evs) {
      const q = this.toScene(ev.clientX, ev.clientY);
      this.eraseSweep(from, q);
      from = q;
    }
    const p = this.toScene(e.clientX, e.clientY);
    this.eraseLast = p;
    this.updateTints(p);
    this.canvas.requestRenderAll();
    this.renderEraseFrame(p);
  }

  /** Pointer-up: commit the whole drag as ONE transaction (plan F2). */
  private endErase(): void {
    this.eraseActive = false;
    this.eraseId = null;
    const at = this.eraseLast;
    this.eraseLast = null;
    this.commitErase();
    // Keep the hover ring where the eraser rests (unless the tool changed).
    if (this.tool === "eraser" && at) this.drawEraserCursor(at);
    else this.clearPreview();
  }

  /** Esc / pointercancel while erasing: throw the drag away and restore the page
   * exactly as it was — unhide every pending object, drop every preview, touch
   * neither the model nor the network. */
  private cancelErase(): void {
    this.eraseActive = false;
    this.eraseId = null;
    const at = this.eraseLast;
    this.eraseLast = null;
    this.restorePending();
    this.canvas.requestRenderAll();
    if (this.tool === "eraser" && at) this.drawEraserCursor(at);
    else this.clearPreview();
  }

  /** Unhide + un-fade everything the (cancelled) drag had staged, and clear it. */
  private restorePending(): void {
    this.restoreAllTints();
    for (const pe of this.pendingErase.values()) pe.obj.set({ visible: true });
    this.pendingErase.clear();
  }

  private pushSample(e: PointerEvent | MouseEvent): void {
    const p = this.toScene(e.clientX, e.clientY);
    const pressure = "pressure" in e ? e.pressure : 0.5;
    // A stroke that starts on the paper and runs off its left edge is clamped to
    // the edge rather than dropped: flattening the tail is a smaller loss than
    // losing the whole stroke because it overshot.
    const x = this.infinite ? clampWritableX(p.x) : p.x;
    const y = this.infinite ? clampWritableY(p.y) : p.y;
    this.strokeSamples.push({ x, y, pressure: clampPressure(pressure) });
  }

  /** Rewrite the PREVIEW-ONLY prediction set from the browser's predicted
   * continuation of the active pointer stream (getPredictedEvents — the other
   * half of "ink glued to the pen tip", plan §3.1). The reusable array is
   * cleared and refilled in place, so every batch of real samples drops the
   * previous predictions; nothing here ever reaches strokeSamples or a commit.
   *
   * PEN ONLY. Prediction buys back the latency between a stylus touching glass
   * and the OS delivering the event — real, and worth guessing ahead for. A
   * mouse has no such gap: the cursor IS the input, so a predicted continuation
   * is a line drawn past where the user actually is, rewritten on every move.
   * That is the snapping tail people saw when drawing with a mouse — guessing
   * ahead of an input that is already exact can only ever be wrong. */
  private pushPredicted(e: PointerEvent): void {
    this.predicted.length = 0;
    if (!this.activePen) return;
    const evs: PointerEvent[] = (e as any).getPredictedEvents?.() ?? [];
    for (const ev of evs) {
      const p = this.toScene(ev.clientX, ev.clientY);
      this.predicted.push({ x: p.x, y: p.y, pressure: clampPressure(ev.pressure) });
    }
    if (penDebugEnabled() && this.predicted.length !== this.lastPredLog) {
      this.lastPredLog = this.predicted.length;
      penLog(`  predict ${this.predicted.length}`);
    }
  }

  private pushTouchSample(touch: SafariTouch): void {
    const p = this.toScene(touch.clientX, touch.clientY);
    this.strokeSamples.push({
      x: p.x,
      y: p.y,
      pressure: clampPressure(touch.force),
    });
  }

  private firstStylusTouch(touches: TouchList): SafariTouch | null {
    for (let i = 0; i < touches.length; i++) {
      const touch = touches.item(i) as SafariTouch | null;
      if (touch?.touchType === "stylus") return touch;
    }
    return null;
  }

  private touchById(touches: TouchList, id: number): SafariTouch | null {
    for (let i = 0; i < touches.length; i++) {
      const touch = touches.item(i) as SafariTouch | null;
      if (touch?.identifier === id) return touch;
    }
    return null;
  }

  /** Draw the in-flight stroke on the overlay (variable-width when pressure is
   * on and a pen is writing; a uniform line otherwise). */
  private drawPreview(): void {
    const ctx = this.previewCtx;
    if (!ctx) return;
    this.wipePreview();
    // Mark dirty AFTER the wipe, or the wipe clears the flag and the paint below
    // survives the next clearPreview.
    this.previewInk = true;
    const samples = this.strokeSamples;
    if (samples.length < 1) return;
    // Lasso: a dashed marquee following the hand-drawn loop, with a faint fill,
    // so the user sees exactly what will be scanned.
    if (this.tool === "lasso") {
      ctx.save();
      ctx.beginPath();
      samples.forEach((s, i) => (i === 0 ? ctx.moveTo(s.x, s.y) : ctx.lineTo(s.x, s.y)));
      ctx.closePath();
      ctx.fillStyle = "rgba(224,177,92,0.10)";
      ctx.fill();
      ctx.setLineDash([6, 4]);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = "rgba(224,177,92,0.95)";
      ctx.stroke();
      ctx.restore();
      return;
    }
    const highlighter = this.tool === "highlighter";
    const size = Math.max(1, this.inkWidthRel() * this.W);
    // The wet-ink preview extends the real samples with the OS-predicted
    // continuation (§3.1) — preview only; finishStroke commits strokeSamples.
    const live = this.predicted.length
      ? joinLivePreview(samples, this.predicted, this.previewJoin)
      : samples;
    // The highlighter is flat and translucent — never pressure-shaped.
    ctx.globalAlpha = highlighter ? HIGHLIGHT_OPACITY : 1;
    if (this.pressure && this.activePen && !highlighter) {
      const outline = strokeOutline(live, size, this.smoothing);
      if (outline.length >= 3) {
        ctx.fillStyle = this.color;
        ctx.beginPath();
        outline.forEach(([x, y], i) =>
          i === 0 ? ctx.moveTo(x ?? 0, y ?? 0) : ctx.lineTo(x ?? 0, y ?? 0),
        );
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1;
        return;
      }
    }
    // A single sample (a tap in progress) → a round dot, so the tap shows
    // immediately instead of nothing-until-commit.
    if (live.length < 2) {
      const s = live[0];
      if (s) {
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(s.x, s.y, size / 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      return;
    }
    ctx.strokeStyle = this.color;
    ctx.lineWidth = size;
    ctx.lineCap = highlighter ? "butt" : "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    // Smooth the wet-ink centre-line (quadratic-through-midpoints, Bug C) so the
    // live preview matches the saved render and never looks choppy on a fast
    // drag. O(points), independent of the decimation "smoothing level".
    traceSmooth(ctx, live);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  /** Clear the WHOLE preview bitmap regardless of the ctx transform. A paged
   * editor's transform is a uniform scale (clearing [0..W]×[0..H] covered the whole
   * bitmap), but the infinite camera adds a translation, so a transform-relative
   * clear would miss the panned/negative regions — reset to identity, wipe the raw
   * device bitmap, restore. Safe (and equivalent) for both modes. */
  /** Wipe the bitmap unconditionally. The painters below call this to start a
   *  frame; only `clearPreview` is allowed to consult `previewInk`. */
  private wipePreview(): void {
    const ctx = this.previewCtx;
    if (!ctx) return;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.preview.width, this.preview.height);
    ctx.restore();
    this.previewInk = false;
  }

  /** Wipe the bitmap only if something was drawn into it.
   *
   *  A clear costs page width times page height times the retina factor squared,
   *  per editor. applyTool called it on every tool switch whether or not anything
   *  had been drawn, which on a 20-page note wiped 44.9 megapixels to remove
   *  nothing.
   *
   *  The flag MUST be set after the painter's own wipe, never before it: setting it
   *  first and then calling this would clear the flag and leave the paint on screen.
   *  That is exactly how the eraser ring survived a switch to another tool. */
  private clearPreview(): void {
    if (!this.previewInk) return;
    this.wipePreview();
  }

  /** Draw the eraser's hit radius as a hollow ring that follows the pointer (a
   * hover, outside any drag — a drag frame renders through renderEraseFrame so the
   * split previews ride along). Drawn on the pointer-transparent preview layer. */
  private drawEraserCursor(p: { x: number; y: number }): void {
    const ctx = this.previewCtx;
    if (!ctx) return;
    this.wipePreview();
    // Mark dirty AFTER the wipe, or the wipe clears the flag and the paint below
    // survives the next clearPreview.
    this.previewInk = true;
    this.paintEraserRing(ctx, p);
  }

  /** Commit a finished pen stroke as a saved annotation (variable-width filled
   * outline when it carried real pen pressure, a round dot for a single-sample
   * tap, else a uniform polyline). A deliberate tap DOES ink a dot — only a
   * truly empty stroke (no samples) produces nothing. */
  /** Crop the underlying rendered page (the pdf.js canvas, or the canvas-note
   * surface) to the lasso's bounding box, as a base64 PNG "scan", and hand it to
   * the AI-explain handler. A best-effort approximation — the visible pixels of
   * the circled area. */
  private captureLassoRegion(samples: Sample[]): void {
    if (samples.length < 3 || !this.handlers.onRegion) return;
    if (this.infinite) {
      // Item 8: an infinite canvas has no tile-sized background canvas and its
      // scene coords are unbounded — clamping to [0, W] would be nonsense there.
      this.captureLassoRegionInfinite(samples);
      return;
    }
    const xs = samples.map((s) => s.x);
    const ys = samples.map((s) => s.y);
    const minX = Math.max(0, Math.min(...xs));
    const minY = Math.max(0, Math.min(...ys));
    const maxX = Math.min(this.W, Math.max(...xs));
    const maxY = Math.min(this.H, Math.max(...ys));
    const w = maxX - minX;
    const h = maxY - minY;
    if (w < 8 || h < 8) return; // too small to be meaningful

    // The rendered page canvas is the FIRST <canvas> in the page element (the
    // pdf.js / canvas-note background), appended before our Fabric layers.
    const src = this.pageEl.querySelector("canvas") as HTMLCanvasElement | null;
    let dataUrl: string | null = null;
    if (src && src.width > 0) {
      // Samples are SEED-space px [0..W0]; the source canvas fills the page div
      // (style width/height 100%) at the CURRENT committed scale × DPR and may be
      // a different pixel size than the seed. A seed coord s is the fraction s/W0
      // of the page, so its source pixel is src.width · s/W0 — i.e. the ratio
      // src.width/W0 (the live zoom cancels), robust across zoom/resize.
      const sx = (src.width / this.W) * minX;
      const sy = (src.height / this.H) * minY;
      const sw = (src.width / this.W) * w;
      const sh = (src.height / this.H) * h;
      const out = document.createElement("canvas");
      out.width = Math.max(1, Math.round(sw));
      out.height = Math.max(1, Math.round(sh));
      const octx = out.getContext("2d");
      if (octx) {
        // White matte first so a transparent canvas-note region reads as paper.
        octx.fillStyle = "#ffffff";
        octx.fillRect(0, 0, out.width, out.height);
        try {
          octx.drawImage(src, sx, sy, sw, sh, 0, 0, out.width, out.height);
          dataUrl = out.toDataURL("image/png");
        } catch {
          dataUrl = null; // tainted canvas etc. — fail soft
        }
      }
    }
    this.clearPreview();
    if (!dataUrl) return;
    const b64 = dataUrl.split(",")[1] ?? "";
    this.handlers.onRegion(
      b64,
      { x: minX / this.W, y: minY / this.H, w: w / this.W, h: h / this.H },
      this.page,
    );
  }

  /** Item 8 — camera-aware lasso capture on the INFINITE canvas: the only pixels
   * are Fabric's viewport-sized lower canvas, so intersect the lasso bbox (scene
   * coords, possibly negative/far) with the CURRENT viewport, map through the
   * camera to lower-canvas device pixels (lassoViewportCrop), and crop those —
   * the visible pixels of the circled area, white-matted exactly like the paged
   * path. A lasso fully outside the viewport captures nothing (fail-soft). */
  private captureLassoRegionInfinite(samples: Sample[]): void {
    this.clearPreview(); // the marquee has served its purpose either way
    if (!this.handlers.onRegion) return;
    const src = this.canvas.lowerCanvasEl as HTMLCanvasElement | null;
    const cssW: number = this.canvas.getWidth?.() ?? 0;
    const cssH: number = this.canvas.getHeight?.() ?? 0;
    if (!src || !(src.width > 0) || !(cssW > 0) || !(cssH > 0)) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const s of samples) {
      if (s.x < minX) minX = s.x;
      if (s.y < minY) minY = s.y;
      if (s.x > maxX) maxX = s.x;
      if (s.y > maxY) maxY = s.y;
    }
    const crop = lassoViewportCrop(
      { minX, minY, maxX, maxY },
      { tx: this.camTx, ty: this.camTy, scale: this.camScale },
      { cssW, cssH, pxW: src.width, pxH: src.height },
    );
    if (!crop) return;
    const out = document.createElement("canvas");
    out.width = Math.max(1, Math.round(crop.sw));
    out.height = Math.max(1, Math.round(crop.sh));
    const octx = out.getContext("2d");
    if (!octx) return;
    // White matte first so the transparent canvas region reads as paper.
    octx.fillStyle = "#ffffff";
    octx.fillRect(0, 0, out.width, out.height);
    let dataUrl: string | null = null;
    try {
      octx.drawImage(src, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, out.width, out.height);
      dataUrl = out.toDataURL("image/png");
    } catch {
      dataUrl = null; // tainted canvas etc. — fail soft
    }
    if (!dataUrl) return;
    const b64 = dataUrl.split(",")[1] ?? "";
    // The rect stays tile-normalized like every other stored coordinate — on an
    // infinite canvas it may lie outside [0,1]; the consumer treats it as advisory.
    this.handlers.onRegion(
      b64,
      {
        x: crop.scene.x / this.W,
        y: crop.scene.y / this.H,
        w: crop.scene.w / this.W,
        h: crop.scene.h / this.H,
      },
      this.page,
    );
  }

  private finishStroke(samples: Sample[], wasPen: boolean): void {
    if (samples.length < 1) {
      if (penDebugEnabled()) penLog("  finishStroke: empty — nothing to commit");
      return;
    }
    if (penDebugEnabled()) penLog(`  finishStroke: ${samples.length} samp (pen=${wasPen})`);
    // Lasso: don't ink — scan the circled region of the underlying page and hand
    // the PNG to the AI-explain handler.
    if (this.tool === "lasso") {
      this.captureLassoRegion(samples);
      return;
    }
    const highlighter = this.tool === "highlighter";
    // The highlighter is uniform (never pressure-shaped); the pen uses a filled
    // outline for a real pressure stroke OR a single-sample tap (perfect-freehand
    // draws one point as a round dot). commitInkStroke handles all three.
    this.commitInkStroke(samples, {
      color: this.color,
      widthRel: this.inkWidthRel(),
      opacity: highlighter ? HIGHLIGHT_OPACITY : 1,
      pressure: !highlighter && this.pressure && wasPen,
    });
    this.canvas.requestRenderAll();
  }

  // -- public ---------------------------------------------------------------

  applyTool(tool: AnnotationTool): void {
    const wasSelecting = this.toolApplied && this.tool === "select";
    this.tool = tool;
    // Reflect the ACTIVE tool onto the DOM so callers (and E2E) can observe that
    // the tool switch has actually reached the editor — the store→React→effect
    // hop lands here asynchronously, so the toolbar's pressed state alone doesn't
    // mean a gesture will be handled with this tool yet.
    this.upper.setAttribute("data-active-tool", tool);
    const c = this.canvas;
    const selecting = tool === "select";
    c.selection = selecting;
    // The pen is hand-rolled (see onPointerDown); Fabric's drawing mode is never
    // used (it draws on hover / can miss the pointerup → stray ink).
    c.isDrawingMode = false;
    // The eraser is now hand-rolled (geometric radius hit-test in eraseSweep),
    // so Fabric never needs to target-find for it — only select mode does.
    c.skipTargetFind = !selecting;
    c.perPixelTargetFind = false;
    c.targetFindTolerance = 0;
    c.defaultCursor =
      tool === "pen" ||
      tool === "highlighter" ||
      tool === "text" ||
      tool === "lasso" ||
      isBoxShape(tool) ||
      isSegmentShape(tool) ||
      tool === "fill"
        ? "crosshair"
        : tool === "eraser"
          ? "none" // our own ring cursor stands in for it
          : "default";
    // Every one of the 5 per-object flags below depends on `selecting` ALONE, and
    // each construction site already sets them from the live tool. So the walk is
    // only needed when select mode actually flips. A pen -> highlighter switch
    // leaves all 5 identical, and on a 20-page note at 20,000 strokes the walk
    // costs 20,000 property pairs plus 20,000 setCoords calls to write the values
    // that are already there. None of the 5 sits in Fabric's `cacheProperties`,
    // so the walk never marks an object dirty and never earns its render either.
    const selectingFlipped = selecting !== wasSelecting;
    if (selectingFlipped || !this.toolApplied) {
      for (const obj of c.getObjects()) {
        obj.selectable = selecting;
        obj.evented = selecting;
        // Ink is move-only outside select mode (scaling locked). In select mode it
        // becomes fully transformable — move AND resize — so a selection can be
        // scaled (plan §3 item 3). Rotation stays locked (the plan is move/resize/
        // delete, not rotate); text and highlights keep their own default controls.
        if (obj.annType === "ink") {
          obj.set({ lockScalingX: !selecting, lockScalingY: !selecting, hasControls: selecting });
          obj.setCoords?.();
        }
      }
    }
    // A tool switch mid-erase-drag abandons the drag: restore everything it had
    // staged (hidden objects, previews, tints) — never commit a half-drag. A
    // half-dragged SHAPE is dropped for the same reason.
    let painted = selectingFlipped || !this.toolApplied;
    if (this.eraseActive) {
      this.cancelErase();
      painted = true;
    }
    if (this.shapeDrag) {
      this.endShape(false);
      painted = true;
    }
    // The preview is its OWN bitmap, so clearing it needs no Fabric render.
    if (tool !== "eraser") this.clearPreview(); // drop any stale eraser ring
    if (!selecting && c.getActiveObject()) {
      c.discardActiveObject();
      painted = true;
    }
    this.toolApplied = true;
    // A switch inside one class changes no pixel on the Fabric canvas. Asking for
    // a render there costs a full clear of the lower canvas plus one drawImage per
    // cached object, for a picture identical to the one already on screen.
    if (painted) c.requestRenderAll();
  }

  /** Delete the current selection — a single stroke or a marquee ActiveSelection —
   * as ONE model transaction → one undo entry → one batch write (plan §3 item 3).
   * Returns whether anything was deleted (so the key handler can preventDefault). */
  deleteSelection(): boolean {
    const active: any = this.canvas.getActiveObject();
    if (!active) return false;
    const members = this.membersOf(active) ?? [active];
    const ids: string[] = [];
    for (const obj of members) if (obj.annId) ids.push(obj.annId);
    if (!ids.length) return false;
    // Drop the selection first so removing its members doesn't corrupt the group.
    this.canvas.discardActiveObject();
    for (const obj of members) {
      this.canvas.remove(obj);
      if (obj.annId) this.deindex(obj.annId);
    }
    this.commitOps({ deletes: ids });
    this.canvas.requestRenderAll();
    return true;
  }

  setColor(color: string): void {
    const changed = color !== this.color;
    this.color = color;
    // The config effect re-pushes EVERY tool field on any change; only a genuine
    // colour change may repaint the selection, so an unrelated edit (a width
    // change, a tool switch) can never silently recolour the selected object.
    if (!changed) return;
    // Recolor a selected object (ink stroke vs. text/highlight fill) and persist.
    const obj: any = this.canvas.getActiveObject();
    if (obj?.annId) {
      // Pressure ink is a filled outline (fill = colour); plain ink is a stroke.
      // A drawn shape is outline-only, so its colour is its STROKE — setting
      // fill would flood it and leave the outline on the old colour.
      if (obj.annType === "ink") obj.set(obj.strokeAbs ? { fill: color } : { stroke: color });
      else if (obj.annRings) obj.set({ fill: color });
      else if (obj.annSegment || obj.annBox) obj.set({ stroke: color });
      else obj.set({ fill: color });
      this.canvas.requestRenderAll();
      this.persistUpdate(obj.annId, { color });
    }
  }

  setFont(font: string): void {
    const changed = font !== this.font;
    this.font = font;
    if (!changed) return; // a re-push of the unchanged default must not restyle the selection
    this.applyToActiveText({ fontFamily: fontStack(font) }, { font_family: font });
  }

  setFontSize(rel: number): void {
    const changed = rel !== this.fontSizeRel;
    this.fontSizeRel = rel;
    if (!changed) return; // a re-push of the unchanged default must not resize the selected text
    this.applyToActiveText(
      { fontSize: Math.max(MIN_FONT_PX, rel * this.H), scaleY: 1, scaleX: 1 },
      { font_size_rel: rel },
    );
  }

  setStrokeWidth(rel: number): void {
    const changed = rel !== this.strokeRel;
    this.strokeRel = rel;
    // The config effect re-pushes every field on ANY change; only a genuine width
    // change may retarget the selection, so an unrelated edit (recolour, tool
    // switch) never silently resizes — or, for a pressure stroke, re-outlines and
    // re-persists — the selected stroke (plan §3.6).
    if (!changed) return;
    // Live-resize a selected ink stroke (plan §3.6): a plain polyline restyles
    // its strokeWidth; a PRESSURE stroke re-derives its filled outline from the
    // stored samples at the new nominal width — no more delete + redraw.
    const obj: any = this.canvas.getActiveObject();
    if (!obj?.annId) return;
    // A selected shape re-weights the same way a plain polyline does — it is
    // stroked geometry, and it shares the pen's width control on the toolbar.
    // `strokeUniform` keeps the new width honest under any select-mode scale.
    if (obj.annSegment || obj.annBox) {
      obj.set({ strokeWidth: shapeStrokeWidth(rel, this.W) });
      obj.annStrokeRel = rel;
      this.canvas.requestRenderAll();
      this.persistUpdate(obj.annId, { stroke_rel: rel });
      return;
    }
    if (obj.annType !== "ink") return;
    if (!obj.strokeAbs) {
      obj.set({ strokeWidth: Math.max(1, rel * this.W) });
      this.canvas.requestRenderAll();
      this.persistUpdate(obj.annId, { stroke_rel: rel });
      return;
    }
    this.resizePressureStroke(obj, rel);
  }

  /** Recompute a committed pressure stroke's outline polygon at a new width
   * (plan §3.6). The stored samples are the durable truth — they are mapped
   * through the object's CURRENT transform (a select-mode move) into absolute
   * seed coords, never resampled — and the polygon's points are swapped for the
   * re-derived outline in place. Folding the transform into the samples and
   * letting Fabric re-derive the bbox from the new points keeps the stroke
   * exactly where it sits on screen; only its thickness changes. */
  private resizePressureStroke(obj: any, rel: number): void {
    const m = obj.calcTransformMatrix();
    const off = obj.pathOffset ?? { x: 0, y: 0 };
    const abs: Sample[] = (obj.strokeAbs as Sample[]).map((p) => {
      const q = this.fabric.util.transformPoint(new this.fabric.Point(p.x - off.x, p.y - off.y), m);
      return { x: q.x, y: q.y, pressure: p.pressure ?? 0.5 };
    });
    const outline = strokeOutline(abs, Math.max(1, rel * this.W), this.smoothing);
    if (outline.length < 3) return; // nothing renderable at this width — keep as-is
    // The transform was folded into `abs`, so the new points live in the absolute
    // seed frame: reset the (translation-only — scaling/rotation are locked)
    // transform and let Fabric recompute bbox + pathOffset + position from them.
    obj.set({ angle: 0, scaleX: 1, scaleY: 1 });
    obj.points = outline.map(([x, y]) => ({ x: x ?? 0, y: y ?? 0 }));
    obj.setBoundingBox(true);
    obj.dirty = true; // points were swapped directly — invalidate the render cache
    obj.strokeAbs = abs;
    obj.annStrokeRel = rel;
    obj.setCoords();
    this.canvas.requestRenderAll();
    // The samples' absolute positions are unchanged (any move was already
    // persisted by object:modified), so the width is the only field to sync.
    this.persistUpdate(obj.annId, { stroke_rel: rel });
    this.indexObject(obj); // its bbox just grew/shrank — re-home it in the grid
  }

  /** Live smoothing preset (§3.7): read by the preview AND the commit path, so
   * what the pen shows is exactly what persists. Saved annotations carry only
   * their raw points — the preset itself is a session preference. */
  /** Shape styling for the next shape, and for a SELECTED one — restyling what
   * you already drew is the same gesture as restyling before you draw. */
  setShapeStyle(fill: string | null, dash: boolean, corner: number): void {
    const changed =
      fill !== this.shapeFill || dash !== this.shapeDash || corner !== this.shapeCorner;
    this.shapeFill = fill;
    this.shapeDash = dash;
    this.shapeCorner = corner;
    if (!changed) return;
    const obj: any = this.canvas.getActiveObject();
    if (!obj?.annId || !(obj.annBox || obj.annSegment)) return;
    obj.annFill = fill;
    obj.annDash = dash;
    obj.annCorner = corner;
    this.applyShapeStyle(obj);
    this.canvas.requestRenderAll();
    this.persistUpdate(obj.annId, { fill_color: fill ?? undefined, dash, corner_radius: corner });
  }

  /** Push an object's stored style onto its Fabric properties (and, for a
   * rounded rect, rebuild its path — the radius is geometry, not paint). */
  private applyShapeStyle(obj: any): void {
    const width = obj.strokeWidth ?? 2;
    obj.set({
      fill: obj.annFill || "",
      strokeDashArray: obj.annDash ? dashPattern(width) : null,
    });
    if (obj.annType === "rect" && obj.annBox) {
      const d = boxShapePath("rect", obj.annBox, obj.annCorner ?? 0);
      if (d) {
        const fresh: any = new this.fabric.Path(d, {});
        obj.set({ path: fresh.path });
      }
    }
    obj.setCoords?.();
  }

  setSmoothing(preset: SmoothingPreset): void {
    this.smoothing = preset;
  }

  setHighlighterWidth(rel: number): void {
    this.highlighterRel = rel;
  }

  setEraserRadius(rel: number): void {
    // The radius is read live in eraseSweep/eraserRadiusPx, so nothing else to do.
    this.eraserRel = rel;
  }

  setEraseMode(mode: EraseMode): void {
    this.eraseMode = mode;
  }

  setPressure(on: boolean): void {
    this.pressure = on;
  }

  /** Live-update the selected text object (if any) and persist the change. */
  private applyToActiveText(fabricProps: Record<string, unknown>, patch: Partial<AnnotationDraft>): void {
    const obj: any = this.canvas.getActiveObject();
    if (!obj || (obj.annType !== "text" && obj.annType !== "note")) return;
    obj.set(fabricProps);
    // readGeometry below re-derives font_size_rel and overrides the patch, so the
    // stash has to move first or a size under the render floor is written back
    // floored. See MIN_FONT_PX in fonts.ts.
    if (patch.font_size_rel != null) obj.annFontSizeRel = patch.font_size_rel;
    obj.setCoords?.();
    this.canvas.requestRenderAll();
    if (obj.annId) this.persistUpdate(obj.annId, { ...patch, ...this.readGeometry(obj) });
  }

  destroy(): void {
    this.torn = true;
    // Commit any typed text that is still mid-edit: exitEditing() fires the
    // existing "text:editing:exited" handler, which persists (or deletes empty).
    try {
      const active: any = this.canvas.getActiveObject();
      if (active?.isEditing) active.exitEditing();
    } catch {
      /* ignore — teardown must complete */
    }
    // A stroke in flight when the editor is torn down (e.g. a re-raster rebuild
    // mid-write) would otherwise be dropped; finalize it so the ink commits.
    try {
      if (this.activeStrokeId !== null) this.finalizeActiveStroke(true);
    } catch {
      /* ignore — teardown must complete */
    }
    // A drag mid-erase must not commit a half-finished sweep on teardown; restore
    // the hidden/faded objects so nothing is silently lost.
    try {
      if (this.eraseActive) this.cancelErase();
    } catch {
      /* ignore — teardown must complete */
    }
    // Always release the session-wide flag, even if the steps above threw: a
    // strokeActive left stuck true freezes zoom commits and makes every touch
    // read as a palm for the rest of the session.
    try {
      setPenStrokeActive(false);
    } catch {
      /* ignore — teardown must complete */
    }
    try {
      // Persistence (parked writes, replay) now lives in the InkModel, which
      // ReaderTab owns for the whole document — it OUTLIVES this editor, so a
      // teardown (zoom rebuild, page leaving the window) never drops an unsynced
      // write the way the old per-editor queue did.
      this.removeListeners?.();
      this.preview.remove();
      this.canvas.dispose();
    } catch {
      /* ignore */
    }
  }

  // -- events (non-pen tools via Fabric) ------------------------------------

  private wireEvents(): void {
    const c = this.canvas;

    // Text creation runs through Fabric's pointer pipeline. (The pen, highlighter
    // AND eraser are all hand-rolled — see onPointerDown / beginErase.)
    c.on("mouse:down", (opt: any) => {
      if (this.tool === "text") {
        const p = this.toScene(opt.e.clientX, opt.e.clientY);
        this.addText(p.x, p.y);
      }
    });

    // Select mode: persist a moved/resized object AND re-home it in the eraser
    // index (its bbox just changed). A marquee of N objects (an ActiveSelection)
    // commits ALL of them as one transaction; a single scaled ink stroke bakes the
    // transform into its durable geometry so what persists matches the screen.
    c.on("object:modified", (opt: any) => {
      const t = opt.target;
      if (!t) return;
      const members = this.membersOf(t);
      if (members) {
        this.persistGroupModified(members);
        return;
      }
      if (!t.annId) return;
      const patch =
        t.annType === "ink" && !this.isDotObj(t) && this.wasScaled(t)
          ? this.bakeInkTransform(t)
          : this.readGeometry(t);
      this.persistUpdate(t.annId, patch);
      this.indexObject(t);
      this.canvas.requestRenderAll();
    });

    // A marquee ActiveSelection is move / resize / delete only — never rotate. The
    // ink objects lock their OWN rotation, but the selection is a separate Fabric
    // object that ships a rotate handle by default; a rotated group can't be
    // represented by the axis-aligned rect model (a rotated text/highlight would
    // persist a wrong AABB), so lock it and hide the handle the moment a multi-object
    // selection forms or changes. A single tagged object (no getObjects) keeps its
    // own control set.
    const lockSelectionRotation = () => {
      const a: any = c.getActiveObject();
      if (a && typeof a.getObjects === "function") {
        a.lockRotation = true;
        a.setControlsVisibility?.({ mtr: false });
      }
    };
    c.on("selection:created", lockSelectionRotation);
    c.on("selection:updated", lockSelectionRotation);

    // Typed text is saved when editing ends.
    c.on("text:editing:exited", (opt: any) => {
      const obj = opt.target;
      const value = (obj.text ?? "").trim();
      if (!value) {
        c.remove(obj);
        if (obj.annId) {
          this.deindex(obj.annId);
          this.persistDelete(obj.annId);
        }
        return;
      }
      const geom = this.readGeometry(obj);
      if (obj.annId) {
        // A click in and a click out with no edit used to persist anyway. That
        // write is not free: it costs a request, and it is the step that let a
        // floored font size creep upward once per session.
        const sig = textSig(geom.rect?.x, geom.rect?.y, geom.text, geom.font_size_rel);
        if (obj.annSavedSig === sig) return;
        obj.annSavedSig = sig;
        if (geom.font_size_rel != null) obj.annFontSizeRel = geom.font_size_rel;
        this.persistUpdate(obj.annId, geom);
        this.indexObject(obj);
      } else {
        void this.create(obj, {
          page: this.page,
          type: "text",
          text: value,
          color: this.color,
          font_family: this.font,
          font_size_rel: this.fontSizeRel,
          ...geom,
        });
      }
    });
  }

  // -- eraser: broad-phase index + true-geometry narrow phase (plan F2) -------

  /** Register / re-home an object's SEED-space bbox in the eraser grid. Called
   * wherever an object enters the canvas or its geometry changes (seeding, commit,
   * external add/replace, object:modified). No annId → not yet indexable → skip. */
  private indexObject(obj: any): void {
    if (!obj?.annId) return;
    const bbox = this.objBbox(obj);
    if (bbox) this.grid.insert(obj.annId, bbox);
  }

  private deindex(id: string): void {
    this.grid.remove(id);
  }

  /** One object's true geometry in SEED coordinates (the immutable frame the eraser
   * sweep also lives in): an ink polyline (+ half-width) or a filled rect. Mirrors
   * readGeometry's coordinate handling — ink via the object's transform matrix (so
   * a select-mode move is respected), rect-likes via their seed left/top/size,
   * never a viewport-scaled bounding box (which is what made rect erasing wrong). */
  private seedGeom(obj: any): SeedGeom | null {
    // Shapes are OUTLINES: the eraser must catch the drawn line, not the empty
    // interior of a big rectangle (a filled-rect hit test would delete it from
    // a touch anywhere inside).
    if (obj.annRings) {
      // A fill is a solid area, so — unlike an outline shape — the eraser hits
      // it anywhere inside, which is what "erase this patch of colour" means.
      const box = obj.getBoundingRect?.();
      if (!box || !(box.width > 0) || !(box.height > 0)) return null;
      return { kind: "rect", rect: { left: box.left, top: box.top, width: box.width, height: box.height } };
    }
    if (obj.annSegment || obj.annBox) return this.shapeSeedGeom(obj);
    if (obj.annType === "ink") {
      const m = obj.calcTransformMatrix();
      const off = obj.pathOffset ?? { x: 0, y: 0 };
      const map = (p: { x: number; y: number; pressure?: number }): Sample => {
        const abs = this.fabric.util.transformPoint(new this.fabric.Point(p.x - off.x, p.y - off.y), m);
        return { x: abs.x, y: abs.y, pressure: p.pressure ?? 0.5 };
      };
      const src: any[] = obj.strokeAbs ?? obj.points ?? [];
      if (src.length) {
        const widthPx = obj.strokeAbs
          ? Math.max(1, (obj.annStrokeRel ?? this.strokeRel) * this.W)
          : (obj.strokeWidth ?? 2);
        return { kind: "poly", pts: src.map(map), half: widthPx / 2 };
      }
      // A dot (Circle) has no points — use its centre + radius.
      const ctr = obj.getCenterPoint();
      return { kind: "poly", pts: [{ x: ctr.x, y: ctr.y, pressure: 0.5 }], half: obj.radius ?? 2 };
    }
    // highlight / text / note — a rotation-locked axis-aligned rect in seed px.
    const w = (obj.width ?? 0) * (obj.scaleX ?? 1);
    const h = (obj.height ?? 0) * (obj.scaleY ?? 1);
    if (!(w > 0) || !(h > 0)) return null;
    return { kind: "rect", rect: { left: obj.left ?? 0, top: obj.top ?? 0, width: w, height: h } };
  }

  /** The SEED-space bbox of an object (poly extents inflated by half-width, or the
   * rect itself) — what the grid keys on. */
  private objBbox(obj: any): Bbox | null {
    const geom = this.seedGeom(obj);
    if (!geom) return null;
    if (geom.kind === "rect") {
      const r = geom.rect;
      return { minX: r.left, minY: r.top, maxX: r.left + r.width, maxY: r.top + r.height };
    }
    const pts = geom.pts;
    if (!pts.length) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of pts) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    return { minX: minX - geom.half, minY: minY - geom.half, maxX: maxX + geom.half, maxY: maxY + geom.half };
  }

  /** Sweep the eraser capsule (segment from→to, radius R) for one sub-segment:
   * broad-phase from the grid, then a TRUE-geometry test per candidate, staging a
   * hit LOCALLY (hide / area-split preview) — never a model or network mutation
   * (that is the pointer-up commit's job). No bounding-box-only hits anywhere. */
  private eraseSweep(from: Vec, to: Vec): void {
    const R = this.eraserRadiusPx();
    for (const id of this.grid.queryCapsule(from, to, R)) {
      const existing = this.pendingErase.get(id);
      if (existing?.kind === "whole") continue; // already fully hidden this drag
      const obj = this.findByAnnId(id);
      if (!obj || !obj.annId) continue;
      if (existing?.kind === "split") {
        // Keep eroding an already-split stroke's SURVIVING runs (its original
        // geometry is hidden/stale now).
        this.applyAreaHit(id, obj, from, to, R);
        continue;
      }
      const geom = this.seedGeom(obj);
      if (!geom) continue;
      const hit =
        geom.kind === "rect"
          ? capsuleTouchesRect(from, to, R, geom.rect)
          : capsuleTouchesPolyline(from, to, R + geom.half, geom.pts);
      if (!hit) continue;
      if (this.eraseMode === "area" && obj.annType === "ink") this.applyAreaHit(id, obj, from, to, R);
      else this.hideWhole(id, obj);
    }
  }

  /** Stage a whole-object erase: un-fade, hide it, remember it for the commit. */
  private hideWhole(id: string, obj: any): void {
    this.clearTint(id);
    obj.set({ visible: false });
    this.pendingErase.set(id, { kind: "whole", obj });
  }

  /** Stage / continue an area-erase of one ink stroke: on first contact capture the
   * full stroke as a single run and hide the original; every hit then erodes the
   * SURVIVING runs by the capsule. Nothing commits until pointer-up. */
  private applyAreaHit(id: string, obj: any, from: Vec, to: Vec, R: number): void {
    let entry = this.pendingErase.get(id);
    if (!entry || entry.kind !== "split") {
      const geom = this.seedGeom(obj);
      if (!geom || geom.kind !== "poly" || geom.pts.length < 2) {
        // A dot / 1-point stroke can't be split — erase it whole.
        this.hideWhole(id, obj);
        return;
      }
      this.clearTint(id);
      const style = this.inkStyleOf(obj);
      obj.set({ visible: false });
      entry = { kind: "split", obj, runs: [geom.pts.slice()], style };
      this.pendingErase.set(id, entry);
    }
    const reach = R + entry.style.half;
    entry.runs = this.eraseRunsWithCapsule(entry.runs, from, to, reach);
  }

  /** Drop every point within `reach` of the capsule segment, splitting each run at
   * the gaps — the point-granular area erase (same span test the old code used). */
  private eraseRunsWithCapsule(runs: Sample[][], from: Vec, to: Vec, reach: number): Sample[][] {
    const out: Sample[][] = [];
    for (const run of runs) {
      let cur: Sample[] = [];
      for (const p of run) {
        if (segPointDist(from, to, p) > reach) cur.push(p);
        else if (cur.length) {
          out.push(cur);
          cur = [];
        }
      }
      if (cur.length) out.push(cur);
    }
    return out;
  }

  /** Recover an ink stroke's style from its live Fabric object (colour, width,
   * opacity, pressure, half-width) so its fragments rebuild identically. */
  private inkStyleOf(obj: any): InkStyle {
    const pressure = !!obj.strokeAbs;
    const color: string = pressure ? obj.fill : obj.stroke || obj.fill || this.color;
    const opacity: number = obj.annOpacity ?? 1;
    const widthRel: number = obj.annStrokeRel ?? (obj.strokeWidth ? obj.strokeWidth / this.W : this.strokeRel);
    return { color, widthRel, opacity, pressure, half: Math.max(1, widthRel * this.W) / 2 };
  }

  // -- eraser: preview tinting (OneNote affordance) ---------------------------

  /** Fade candidates the eraser is hovering over (within ~1.5×R of the point) so
   * the user sees what a lift would erase, and un-fade any it moved away from.
   * Confirmed hits are excluded (they are hidden, not tinted). The stored ORIGINAL
   * opacity is what's restored, so a tint can never leak into a persisted value. */
  private updateTints(p: Vec): void {
    const reach = this.eraserRadiusPx() * 1.5;
    const near = new Set<string>();
    for (const id of this.grid.queryCapsule(p, p, reach)) {
      if (this.pendingErase.has(id)) continue;
      const obj = this.findByAnnId(id);
      if (!obj || obj.visible === false) continue;
      const geom = this.seedGeom(obj);
      if (!geom) continue;
      const touches =
        geom.kind === "rect"
          ? segRectDist(p, p, geom.rect) <= reach
          : capsuleTouchesPolyline(p, p, reach + geom.half, geom.pts);
      if (!touches) continue;
      near.add(id);
      if (!this.eraseTint.has(id)) {
        this.eraseTint.set(id, { obj, opacity: obj.opacity ?? 1 });
        obj.set({ opacity: 0.25 });
      }
    }
    for (const [id, t] of [...this.eraseTint]) {
      if (!near.has(id)) {
        t.obj.set({ opacity: t.opacity });
        this.eraseTint.delete(id);
      }
    }
  }

  private clearTint(id: string): void {
    const t = this.eraseTint.get(id);
    if (t) {
      t.obj.set({ opacity: t.opacity });
      this.eraseTint.delete(id);
    }
  }

  private restoreAllTints(): void {
    for (const t of this.eraseTint.values()) t.obj.set({ opacity: t.opacity });
    this.eraseTint.clear();
  }

  // -- eraser: preview rendering ----------------------------------------------

  /** Redraw the erase preview layer: the surviving spans of every area-split (the
   * originals are hidden), then the eraser ring at the pointer. */
  private renderEraseFrame(p: Vec): void {
    const ctx = this.previewCtx;
    if (!ctx) return;
    this.wipePreview();
    // Mark dirty AFTER the wipe, or the wipe clears the flag and the paint below
    // survives the next clearPreview.
    this.previewInk = true;
    for (const pe of this.pendingErase.values()) {
      if (pe.kind === "split") for (const run of pe.runs) this.drawRunPreview(ctx, run, pe.style);
    }
    this.paintEraserRing(ctx, p);
  }

  /** Draw one surviving span as a live preview (a plain polyline in the stroke's
   * colour/width — the committed object re-derives its true pressure outline). */
  private drawRunPreview(ctx: CanvasRenderingContext2D, run: Sample[], style: InkStyle): void {
    if (run.length < 1) return;
    ctx.save();
    ctx.globalAlpha = style.opacity;
    ctx.strokeStyle = style.color;
    ctx.fillStyle = style.color;
    const size = Math.max(1, style.widthRel * this.W);
    ctx.lineWidth = size;
    ctx.lineCap = style.opacity < 1 ? "butt" : "round";
    ctx.lineJoin = "round";
    if (run.length < 2) {
      const s = run[0]!;
      ctx.beginPath();
      ctx.arc(s.x, s.y, size / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.beginPath();
      run.forEach((s, i) => (i === 0 ? ctx.moveTo(s.x, s.y) : ctx.lineTo(s.x, s.y)));
      ctx.stroke();
    }
    ctx.restore();
  }

  /** Paint the eraser's hollow ring + centre dot at p onto the given ctx (the
   * caller owns clearing). Dashed in area mode so it reads differently. */
  private paintEraserRing(ctx: CanvasRenderingContext2D, p: Vec): void {
    const r = this.eraserRadiusPx();
    ctx.save();
    ctx.strokeStyle = "rgba(120,120,120,0.95)";
    ctx.fillStyle = "rgba(200,200,200,0.18)";
    ctx.lineWidth = 1.5;
    if (this.eraseMode === "area") ctx.setLineDash([5, 4]);
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "rgba(80,80,80,0.7)";
    ctx.beginPath();
    ctx.arc(p.x, p.y, 1.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // -- eraser: single-transaction commit --------------------------------------

  /** Commit the whole erase drag as ONE transaction (plan F2): whole hits become
   * deletes; each area-split's LARGEST fragment keeps the original id via a
   * geometry update, its extra fragments are creates. The Fabric canvas is
   * reconciled to match, and the model commits it all as one undo entry / one batch
   * write. Nothing staged → a no-op. */
  private commitErase(): void {
    this.restoreAllTints(); // never persist a hover fade
    const pending = this.pendingErase;
    this.pendingErase = new Map();
    if (!pending.size) return;

    const deletes: string[] = [];
    const updates: { id: string; patch: Partial<AnnotationDraft> }[] = [];
    const creates: AnnotationDraft[] = [];
    const remove: any[] = []; // originals to drop from the canvas
    const createObjs: any[] = []; // parallel to `creates`, adopt ids after commit
    const keepObjs: { id: string; obj: any }[] = []; // largest fragments (id retained)

    for (const [id, pe] of pending) {
      remove.push(pe.obj);
      if (pe.kind === "whole") {
        deletes.push(id);
        continue;
      }
      // Build fragment objects first and keep only the renderable ones, so the
      // "largest" pick can never land on a run that draws nothing (which would
      // strand the original id on an invisible row).
      const frags = pe.runs
        .filter((r) => r.length >= 1)
        .map((run) => ({ run, obj: this.inkObjFromSamples(run, pe.style) }))
        .filter((f): f is { run: Sample[]; obj: any } => !!f.obj);
      if (!frags.length) {
        deletes.push(id); // fully consumed by the eraser
        continue;
      }
      const largest = largestRunIndex(frags.map((f) => f.run));
      frags.forEach(({ run, obj }, i) => {
        obj.annType = "ink";
        if (i === largest) {
          obj.annId = id; // the largest fragment keeps the original id
          keepObjs.push({ id, obj });
          const draft = this.inkDraftFromSamples(run, pe.style);
          const patch: Partial<AnnotationDraft> = { points: draft.points, stroke_rel: draft.stroke_rel };
          if (draft.opacity != null) patch.opacity = draft.opacity;
          updates.push({ id, patch });
        } else {
          creates.push(this.inkDraftFromSamples(run, pe.style));
          createObjs.push(obj);
        }
      });
    }

    // Reconcile the canvas: drop originals, add the rebuilt fragments.
    for (const o of remove) {
      this.canvas.remove(o);
      if (o.annId) this.deindex(o.annId);
    }
    for (const { obj } of keepObjs) {
      this.canvas.add(obj);
      this.indexObject(obj);
    }
    for (const obj of createObjs) this.canvas.add(obj);
    this.sinkFills();

    // Persist as ONE transaction; adopt the create temp ids onto their objects.
    const ids = this.commitOps({ creates, updates, deletes });
    createObjs.forEach((obj, i) => {
      obj.annId = ids[i] ?? obj.annId;
      this.indexObject(obj);
    });

    this.canvas.requestRenderAll();
  }

  /** Route an op set through the single-transaction handler when present (one undo
   * entry, one batch write); otherwise fall back to the per-op handlers so the
   * editor still works without onTransact. Returns the created temp ids in order. */
  private commitOps(ops: TransactOps): string[] {
    if (this.handlers.onTransact) return this.handlers.onTransact(ops);
    const ids = (ops.creates ?? []).map((d) => this.handlers.onCreate(d));
    for (const u of ops.updates ?? []) this.handlers.onUpdate(u.id, u.patch);
    for (const d of ops.deletes ?? []) this.handlers.onDelete(d);
    return ids;
  }

  // -- ink object / draft builders --------------------------------------------

  /** Build a Fabric ink object from absolute samples with an explicit style: a
   * pressure outline (or a dot) when pressure is on, else a plain polyline. Returns
   * null only when there is nothing renderable. Shared by finishStroke's commit and
   * the eraser's fragment rebuild, so both produce identical objects. */
  private inkObjFromSamples(
    samples: Sample[],
    style: { color: string; widthRel: number; opacity: number; pressure: boolean },
  ): any | null {
    if (samples.length < 1) return null;
    const highlighter = style.opacity < 1;
    const usePressureOutline = style.pressure && !highlighter;
    if (usePressureOutline || samples.length < 2) {
      try {
        const size = Math.max(1, style.widthRel * this.W);
        // The commit uses the SAME smoothing preset the preview drew with (§3.7),
        // so the persisted object is pixel-identical to the wet ink.
        const outline = strokeOutline(samples, size, this.smoothing);
        if (outline.length >= 3) {
          return this.pressureInkObject(outline, style.color, samples, style.opacity, style.widthRel);
        }
      } catch {
        /* fall through to a uniform polyline */
      }
    }
    if (samples.length < 2) return null;
    return this.inkObject(samples.map((s) => ({ x: s.x, y: s.y })), style.color, style.widthRel, style.opacity);
  }

  /** The AnnotationDraft for an ink stroke with an explicit style — the exact
   * normalized points (pressure only when a pressure outline is used). */
  private inkDraftFromSamples(
    samples: Sample[],
    style: { color: string; widthRel: number; opacity: number; pressure: boolean },
  ): AnnotationDraft {
    const highlighter = style.opacity < 1;
    const usePressureOutline = style.pressure && !highlighter;
    return {
      page: this.page,
      type: "ink",
      color: style.color,
      stroke_rel: style.widthRel,
      opacity: highlighter ? style.opacity : undefined,
      points: samples.map((p) => ({
        x: p.x / this.W,
        y: p.y / this.H,
        pressure: usePressureOutline ? p.pressure : undefined,
      })),
    };
  }

  /** Build + persist one ink stroke from absolute samples with an explicit style
   * (finishStroke's commit path). */
  private commitInkStroke(
    samples: Sample[],
    style: { color: string; widthRel: number; opacity: number; pressure: boolean },
  ): void {
    const obj = this.inkObjFromSamples(samples, style);
    if (!obj) return;
    this.canvas.add(obj);
    void this.create(obj, this.inkDraftFromSamples(samples, style));
  }

  private addText(x: number, y: number): void {
    const size = Math.max(MIN_FONT_PX, this.fontSizeRel * this.H);
    const it = new this.fabric.IText("", {
      left: x, top: y, fontSize: size, fill: this.color,
      lineHeight: TEXT_LINE_HEIGHT,
      fontFamily: fontStack(this.font), originX: "left", originY: "top",
    });
    this.canvas.add(it);
    this.canvas.setActiveObject(it);
    it.enterEditing();
    it.hiddenTextarea?.focus();
  }

  // -- object construction / geometry --------------------------------------

  private inkObject(
    absPoints: { x: number; y: number }[],
    color: string,
    strokeRel = this.strokeRel,
    opacity = 1,
  ): any {
    const highlighter = opacity < 1;
    const poly: any = new this.fabric.Polyline(absPoints, {
      stroke: color, strokeWidth: Math.max(1, strokeRel * this.W), fill: "",
      opacity,
      strokeLineCap: highlighter ? "butt" : "round", strokeLineJoin: "round",
      originX: "left", originY: "top",
      lockScalingX: true, lockScalingY: true, lockRotation: true,
      hasControls: false, perPixelTargetFind: true,
      selectable: this.tool === "select", evented: this.tool === "select",
      objectCaching: INK_OBJECT_CACHING,
    });
    poly.annType = "ink";
    poly.annStrokeRel = strokeRel;
    poly.annOpacity = opacity;
    return poly;
  }

  /** A variable-width ("pressure") ink stroke: a filled outline polygon. The
   * raw stroke samples (absolute, with pressure) are kept on the object so a
   * move can re-derive the saved points exactly like a polyline. */
  private pressureInkObject(
    outline: number[][],
    color: string,
    strokeAbs: Sample[],
    opacity = 1,
    strokeRel = this.strokeRel,
  ): any {
    const poly: any = new this.fabric.Polygon(
      outline.map(([x, y]) => ({ x: x ?? 0, y: y ?? 0 })),
      {
        fill: color, stroke: "", opacity,
        originX: "left", originY: "top",
        lockScalingX: true, lockScalingY: true, lockRotation: true,
        hasControls: false, perPixelTargetFind: true,
        selectable: this.tool === "select",
        evented: this.tool === "select",
        objectCaching: INK_OBJECT_CACHING,
      },
    );
    poly.annType = "ink";
    poly.strokeAbs = strokeAbs;
    poly.annStrokeRel = strokeRel;
    poly.annOpacity = opacity;
    return poly;
  }

  private toFabric(a: Annotation): any {
    const { W, H } = this;
    if (a.type === "fill") {
      const rings = (a as { rings?: { x: number; y: number }[][] | null }).rings;
      if (!rings?.length) return null;
      const obj = this.fillObject(rings, a.color ?? "#111111", a.opacity ?? 1);
      if (obj) obj.annId = a.id;
      return obj;
    }
    if (a.type === "table") return this.tableFromAnnotation(a);
    if (isBoxShape(a.type) || isSegmentShape(a.type)) return this.shapeFromAnnotation(a);
    if (a.type === "ink" && a.points && a.points.length >= 1) {
      // Pressure stroke OR a single-point dot → re-outline as a filled polygon
      // (matches the overlay); perfect-freehand renders one point as a round dot.
      if (hasPressure(a.points)) {
        const abs = a.points.map((p) => ({ x: p.x * W, y: p.y * H, pressure: clampPressure(p.pressure ?? undefined) }));
        const outline = strokeOutline(abs, Math.max(1, (a.stroke_rel ?? 0.004) * W));
        if (outline.length >= 3) {
          const obj = this.pressureInkObject(outline, a.color ?? "#111111", abs, a.opacity ?? 1, a.stroke_rel ?? 0.004);
          obj.annId = a.id;
          return obj;
        }
      }
      // A plain (no-pressure) single point can't be a polyline → a round dot.
      const dot = a.points[0];
      if (a.points.length < 2 && dot) {
        const r = Math.max(1, (a.stroke_rel ?? 0.004) * W) / 2;
        const obj: any = new this.fabric.Circle({
          left: dot.x * W - r, top: dot.y * H - r, radius: r,
          fill: a.color ?? "#111111", stroke: "", opacity: a.opacity ?? 1,
          originX: "left", originY: "top",
          lockScalingX: true, lockScalingY: true, lockRotation: true,
          hasControls: false,
          selectable: this.tool === "select", evented: this.tool === "select",
          objectCaching: INK_OBJECT_CACHING,
        });
        obj.annId = a.id;
        obj.annType = "ink";
        obj.annStrokeRel = a.stroke_rel ?? 0.004;
        obj.annOpacity = a.opacity ?? 1;
        return obj;
      }
      const obj = this.inkObject(
        a.points.map((p) => ({ x: p.x * W, y: p.y * H })),
        a.color ?? "#111111",
        a.stroke_rel ?? 0.004,
        a.opacity ?? 1,
      );
      obj.annId = a.id;
      obj.annType = "ink";
      return obj;
    }
    if (a.type === "highlight" && a.rect) {
      const obj: any = new this.fabric.Rect({
        left: a.rect.x * W, top: a.rect.y * H, width: a.rect.w * W, height: a.rect.h * H,
        fill: a.color ?? "#111111", opacity: a.opacity ?? HIGHLIGHT_OPACITY,
        originX: "left", originY: "top", lockRotation: true,
        objectCaching: INK_OBJECT_CACHING,
      });
      obj.annId = a.id;
      obj.annType = "highlight";
      return obj;
    }
    if ((a.type === "text" || a.type === "note") && a.rect && a.text) {
      const rel = a.font_size_rel ?? DEFAULT_FONT_SIZE_REL;
      const obj: any = new this.fabric.IText(a.text, {
        left: a.rect.x * W, top: a.rect.y * H,
        fontSize: Math.max(MIN_FONT_PX, rel * H),
        lineHeight: TEXT_LINE_HEIGHT,
        fill: a.color ?? "#1b1f1a", fontFamily: fontStack(a.font_family),
        originX: "left", originY: "top", lockRotation: true,
      });
      obj.annId = a.id;
      obj.annType = a.type;
      // What the ROW holds, which is not always what we render: the floor above
      // can raise the rendered size. readGeometry writes this back untouched so
      // the stored value cannot creep upward. See MIN_FONT_PX in fonts.ts.
      obj.annFontSizeRel = rel;
      // What the row already holds, in the 4 fields a renderer actually reads. An
      // exit that produces the same 4 has nothing to say. Seeded from the ROW and
      // not from a measurement, so a font that lands later cannot make it differ.
      obj.annSavedSig = textSig(a.rect.x, a.rect.y, a.text, rel);
      return obj;
    }
    return null;
  }

  private readGeometry(obj: any): Partial<AnnotationDraft> {
    const { W, H } = this;
    // A shape persists the geometry it was BUILT from, transformed by whatever
    // select mode did to it — the same [0,1] space it loaded from, so a move or
    // resize round-trips exactly.
    if (obj.annSegment || obj.annBox) {
      if (obj.annSegment) {
        const pts = this.shapeAbsPoints(obj);
        // points[0] is the tail and points[1] the tip; an arrow's barbs follow
        // and are re-derived on load, so only the two endpoints persist.
        if (pts && pts.length >= 2) {
          return {
            points: [
              { x: pts[0]!.x / W, y: pts[0]!.y / H },
              { x: pts[1]!.x / W, y: pts[1]!.y / H },
            ],
          };
        }
      }
      const box = this.shapeBoxOf(obj);
      if (box) {
        // Adopt the new box: a table's grid-line hit-test reads `annBox`, so
        // leaving it at the creation box made the boundaries of a MOVED table
        // grabbable only where it used to be.
        obj.annBox = box;
        if (obj.annType === "table") this.repaintTable(obj);
        return { rect: { x: box.x / W, y: box.y / H, w: box.w / W, h: box.h / H } };
      }
      return {};
    }
    if (obj.annType === "ink") {
      if (this.isDotObj(obj)) {
        // A dot (single-sample ink → a Fabric Circle) has no polyline path: persist
        // its absolute centre + effective radius. getCenterPoint folds in any group/
        // scale transform; a select-mode resize grows the radius via scaleX/scaleY.
        const ctr = obj.getCenterPoint();
        const k = (Math.abs(obj.scaleX ?? 1) + Math.abs(obj.scaleY ?? 1)) / 2;
        const r = (obj.radius ?? 1) * k;
        return { points: [{ x: ctr.x / W, y: ctr.y / H }], stroke_rel: (2 * r) / W };
      }
      const m = obj.calcTransformMatrix();
      const off = obj.pathOffset ?? { x: 0, y: 0 };
      // strokeAbs (pressure outline) or points (plain polyline) — both live in
      // the object's pre-transform coordinate frame, so the same matrix maps
      // them to the current canvas position.
      const src: any[] = obj.strokeAbs ?? obj.points ?? [];
      const pts = src.map((p: { x: number; y: number; pressure?: number }) => {
        const abs = this.fabric.util.transformPoint(
          new this.fabric.Point(p.x - off.x, p.y - off.y),
          m,
        );
        const out: { x: number; y: number; pressure?: number } = { x: abs.x / W, y: abs.y / H };
        if (p.pressure != null) out.pressure = p.pressure;
        return out;
      });
      return { points: pts };
    }
    // highlight / text / note — a rotation-locked axis-aligned rect. Standing
    // alone its left/top ARE canvas-seed coords, but as a MEMBER of a marquee
    // ActiveSelection Fabric nests its coords inside the group transform, so
    // left/top are group-RELATIVE and its size is scaled by the group. Reading
    // them raw would persist the box at a group-relative position (teleporting it
    // on reload). Fold the group in through the scene-plane bounding box
    // (getBoundingRect maps aCoords through group.calcTransformMatrix — the same
    // discipline the ink branch uses via calcTransformMatrix), so what persists is
    // the absolute geometry shown. Ungrouped keeps the exact prior fast path.
    const gsY = obj.group ? Math.abs(obj.group.scaleY ?? 1) : 1;
    let left = obj.left ?? 0;
    let top = obj.top ?? 0;
    let w = (obj.width ?? 0) * (obj.scaleX ?? 1);
    let h = (obj.height ?? 0) * (obj.scaleY ?? 1);
    if (obj.group) {
      const br = obj.getBoundingRect();
      left = br.left;
      top = br.top;
      w = br.width;
      h = br.height;
    }
    const rect = { x: left / W, y: top / H, w: w / W, h: h / H };
    if (obj.annType === "text" || obj.annType === "note") {
      // Font size rides the group scale too (a group resize grows the glyphs
      // with the box), so it stays consistent with the scaled rect on reload.
      const scale = (obj.scaleY ?? 1) * gsY;
      const live = (obj.fontSize ?? 0) * scale;
      // The render floor is not a storage floor. When the glyphs are still the
      // floored projection of the stored value, nothing resized this text, so
      // write the stored value back untouched. Deriving live/H instead would
      // store max(MIN_FONT_PX, rel*H)/H, which is LARGER than rel, and
      // text:editing:exited fires on a click in and a click out with no edit,
      // so the size would grow on every session.
      const stored = obj.annFontSizeRel;
      const untouched =
        typeof stored === "number" &&
        Math.abs(live - Math.max(MIN_FONT_PX, stored * H)) < 1e-6;
      return {
        rect,
        text: obj.text,
        font_size_rel: untouched ? stored : live / H,
      };
    }
    return { rect };
  }

  // -- select mode: multi-select move / resize / delete (plan §3 item 3) -------

  /** The member objects of an ActiveSelection (marquee / multi-select), or null when
   * `t` is a single tagged annotation. A single object carries `annId`; an
   * ActiveSelection does not but exposes `getObjects()`. */
  private membersOf(t: any): any[] | null {
    if (!t || t.annId) return null;
    return typeof t.getObjects === "function" ? (t.getObjects() as any[]) : null;
  }

  /** Was this object scaled away from identity (a select-mode resize, vs. a pure
   * move)? Only then does an ink stroke need its transform baked into its geometry. */
  private wasScaled(obj: any): boolean {
    return Math.abs((obj.scaleX ?? 1) - 1) > 1e-3 || Math.abs((obj.scaleY ?? 1) - 1) > 1e-3;
  }

  /** A dot (single-sample ink → a Fabric Circle): no points array and no pressure
   * outline, so it is persisted from its centre + radius, never a polyline path. */
  private isDotObj(obj: any): boolean {
    return obj.annType === "ink" && !obj.strokeAbs && !(obj.points?.length);
  }

  /** Persist a marquee move/scale of N objects as ONE model transaction (plan §3
   * item 3): each member's ABSOLUTE geometry is read through its own transform matrix
   * — Fabric nests member coords inside the ActiveSelection transform and
   * calcTransformMatrix folds that in, the same discipline readGeometry already uses
   * — so one undo entry reverses the whole group and one batch write persists it.
   * Under a group scale an ink stroke's centreline points scale (a reload rebuilds
   * from those); its nominal width is left as-is (a single-object scale bakes width
   * too — see bakeInkTransform). The grid re-homes every moved member. */
  private persistGroupModified(members: any[]): void {
    const updates: { id: string; patch: Partial<AnnotationDraft> }[] = [];
    for (const obj of members) {
      if (!obj.annId) continue;
      updates.push({ id: obj.annId, patch: this.readGeometry(obj) });
    }
    if (!updates.length) return;
    this.commitOps({ updates });
    for (const obj of members) if (obj.annId) this.indexObject(obj);
    this.canvas.requestRenderAll();
  }

  /** Fold a select-mode scale (and any move) into an ink stroke's durable geometry
   * so what persists is what's shown and a reload rebuilds it identically (plan §3
   * item 3). The stored samples/points are mapped through the object's transform into
   * absolute seed coords; a PRESSURE stroke re-derives its filled outline at a width
   * scaled by the object's average scale factor (so its thickness grows with it, like
   * the on-screen scaled polygon), a PLAIN polyline scales its strokeWidth. The scale
   * is then reset to 1 with the baked geometry in place — the same discipline
   * resizePressureStroke uses for a toolbar width change. Rotation is locked for ink,
   * so only translate + scale fold in. Returns the patch (points + stroke_rel). */
  private bakeInkTransform(obj: any): Partial<AnnotationDraft> {
    const k = (Math.abs(obj.scaleX ?? 1) + Math.abs(obj.scaleY ?? 1)) / 2;
    const m = obj.calcTransformMatrix();
    const off = obj.pathOffset ?? { x: 0, y: 0 };
    const src: any[] = obj.strokeAbs ?? obj.points ?? [];
    const abs: Sample[] = src.map((p) => {
      const q = this.fabric.util.transformPoint(new this.fabric.Point(p.x - off.x, p.y - off.y), m);
      return { x: q.x, y: q.y, pressure: p.pressure ?? 0.5 };
    });
    const newRel = Math.max(0.0001, (obj.annStrokeRel ?? this.strokeRel) * k);
    obj.set({ angle: 0, scaleX: 1, scaleY: 1 });
    if (obj.strokeAbs) {
      const outline = strokeOutline(abs, Math.max(1, newRel * this.W), this.smoothing);
      if (outline.length >= 3) obj.points = outline.map(([x, y]) => ({ x: x ?? 0, y: y ?? 0 }));
      obj.strokeAbs = abs;
    } else {
      obj.points = abs.map((p) => ({ x: p.x, y: p.y }));
      obj.set({ strokeWidth: Math.max(1, newRel * this.W) });
    }
    obj.annStrokeRel = newRel;
    obj.setBoundingBox(true);
    obj.dirty = true;
    obj.setCoords();
    // Points now live in the absolute seed frame at identity scale → readGeometry
    // maps them through the translation-only matrix; the new width rides alongside.
    return { ...this.readGeometry(obj), stroke_rel: newRel };
  }

  /** If `obj` is (part of) the current selection, drop that selection first — an
   * external change (undo/redo, reconcile) is about to remove/replace it, and
   * mutating an object still nested in a live ActiveSelection corrupts the group. */
  private discardIfSelected(obj: any): void {
    const active: any = this.canvas.getActiveObject();
    if (!active) return;
    if (active === obj) {
      this.canvas.discardActiveObject();
      return;
    }
    const members = this.membersOf(active);
    if (members && members.includes(obj)) this.canvas.discardActiveObject();
  }

  /** Register a newly drawn/typed object with the model, tagging it with the temp
   * id the model mints synchronously. The model owns batching, id resolution, and
   * offline replay — so the editor no longer parks writes; it just adopts the id. */
  private create(obj: any, draft: AnnotationDraft): void {
    if (penDebugEnabled()) penLog(`  create ${draft.type} (${draft.points?.length ?? 0} pts)`);
    obj.annId = this.handlers.onCreate(draft);
    obj.annType = draft.type;
    this.indexObject(obj); // it just became erasable — home it in the grid
  }

  // -- persistence (fire-and-forget into the model) --------------------------

  private persistUpdate(id: string, patch: Partial<AnnotationDraft>): void {
    this.handlers.onUpdate(id, patch);
  }

  private persistDelete(id: string): void {
    this.handlers.onDelete(id);
  }

  // -- external changes (model events, applied by the owning viewer) ---------

  /** Find a live Fabric object by its annotation id. */
  private findByAnnId(id: string): any | null {
    for (const obj of this.canvas.getObjects() as any[]) {
      if (obj.annId === id) return obj;
    }
    return null;
  }

  /** Add an annotation that entered the model outside this editor (a reconcile of
   * an unseen row, or an undo/redo that re-created a deleted one). Idempotent — an
   * add of an id already on the canvas is treated as a replace, so a doubled event
   * can't stack two objects. */
  applyExternalAdd(a: Annotation): void {
    if (a.page !== this.page) return;
    if (this.findByAnnId(a.id)) {
      this.applyExternalReplace(a);
      return;
    }
    try {
      const obj = this.toFabric(a);
      if (obj) {
        this.canvas.add(obj);
        this.indexObject(obj);
        this.sinkFills();
        this.canvas.requestRenderAll();
      }
    } catch {
      /* never let a malformed external annotation break the editor */
    }
  }

  /** An external event (undo/redo, reconcile, or a definitive persist-reject) has
   * superseded object `id` that a live erase drag had staged (hidden + queued for
   * the pointer-up commit, or merely tinted). Forget the stale staging so the
   * commit never re-deletes an already-gone row, strands a hidden copy, or leaves a
   * duplicate of the fresh object the external path just added. The external path
   * already reconciled the canvas + grid for `id` (removed the old object, added the
   * new one, re-homed the bbox); here we only drop the now-dangling staged refs —
   * exactly the discipline applyIdChange follows when a flush resolves a temp id
   * mid-drag. No opacity restore is needed: the object those entries pointed at was
   * removed by the external path, and any replacement is rebuilt at its true opacity. */
  private dropStaged(id: string): void {
    this.pendingErase.delete(id);
    this.eraseTint.delete(id);
  }

  /** Remove an annotation the model dropped (undo/redo of a create). */
  applyExternalRemove(id: string): void {
    this.deindex(id);
    this.dropStaged(id);
    const obj = this.findByAnnId(id);
    if (!obj) return;
    this.discardIfSelected(obj);
    this.canvas.remove(obj);
    this.canvas.requestRenderAll();
  }

  /** Replace an annotation whose fields changed in the model (undo/redo of an
   * edit, reconcile of an untouched row). Rebuilds the Fabric object from truth. */
  applyExternalReplace(a: Annotation): void {
    if (a.page !== this.page) return;
    const existing = this.findByAnnId(a.id);
    if (existing) {
      this.discardIfSelected(existing);
      this.canvas.remove(existing);
    }
    this.deindex(a.id);
    this.dropStaged(a.id);
    try {
      const obj = this.toFabric(a);
      if (obj) {
        this.canvas.add(obj);
        this.indexObject(obj);
        this.sinkFills();
      }
    } catch {
      /* isolate a bad annotation */
    }
    this.canvas.requestRenderAll();
  }

  /** Adopt the server id the model resolved for a temp-id object, so later
   * edits/erases target the real row. The eraser grid re-keys with it (its bbox is
   * unchanged), so a sweep right after a flush still finds the object. */
  applyIdChange(oldId: string, newId: string): void {
    const obj = this.findByAnnId(oldId);
    if (obj) {
      obj.annId = newId;
      this.deindex(oldId);
      this.indexObject(obj);
    }
    // A flush can resolve an id mid-erase-drag; follow it in the staged sets so
    // the pointer-up commit targets the live row, not the retired temp id.
    const pe = this.pendingErase.get(oldId);
    if (pe) {
      this.pendingErase.delete(oldId);
      this.pendingErase.set(newId, pe);
    }
    const tint = this.eraseTint.get(oldId);
    if (tint) {
      this.eraseTint.delete(oldId);
      this.eraseTint.set(newId, tint);
    }
  }
}
